import Phaser from '../lib/phaser.js';
import { AUDIO_ASSET_KEYS, WORLD_ASSET_KEYS, LOWPOLY_ASSET_KEYS } from '../assets/asset-keys.js';
import { SCENE_KEYS } from './scene-keys.js';
import { Player } from '../world/characters/player.js';
import { getStarterOptions, buildStarterMonster } from '../world/starters.js';
import { DIRECTION } from '../common/direction.js';
import { ENABLE_ZONE_DEBUGGING, TILED_COLLISION_LAYER_ALPHA, TILE_SIZE } from '../config.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from '../utils/data-manager.js';
import {
  getTargetDirectionFromGameObjectPosition,
  getTargetPathToGameObject,
  getTargetPositionFromGameObjectPositionAndDirection,
} from '../utils/grid-utils.js';
import { CANNOT_READ_SIGN_TEXT, SAMPLE_TEXT } from '../utils/text-utils.js';
import { NPC, NPC_MOVEMENT_PATTERN } from '../world/characters/npc.js';
import { WorldMenu } from '../world/world-menu.js';
import { BaseScene } from './base-scene.js';
import { DataUtils } from '../utils/data-utils.js';
import { checkAndEvolveParty } from '../utils/evolution-utils.js';
import { playBackgroundMusic, playSoundFx, playFootstepFx } from '../utils/audio-utils.js';
import { weightedRandom } from '../utils/random.js';
import { Item } from '../world/item.js';
import { RoamingMob } from '../world/roaming-mob.js';
import { BATTLE_FLAG, ENCOUNTER_TILE_TYPE, GAME_EVENT_TYPE, ITEM_CATEGORY, NPC_EVENT_TYPE } from '../types/typedef.js';
import { exhaustiveGuard } from '../utils/guard.js';
import { sleep } from '../utils/time-utils.js';
import { CutsceneScene } from './cutscene-scene.js';
import { DialogScene } from './dialog-scene.js';
import * as TiledUtils from '../utils/tiled-utils.js';
import * as CameraUtils from '../utils/camera-utils.js';
import {
  CUSTOM_TILED_TYPES,
  OBJECT_LAYER_NAMES,
  TILED_AREA_METADATA_PROPERTY,
  TILED_ENCOUNTER_PROPERTY,
  TILED_EVENT_PROPERTY,
  TILED_ITEM_PROPERTY,
  TILED_NPC_PROPERTY,
  TILED_SIGN_PROPERTY,
} from '../assets/tiled-keys.js';
import {
  netManager,
  RemotePlayers,
  ChatOverlay,
  InteractionMenu,
  realmForArea,
  toNetDir,
  REALM_LABELS,
  Tombstones,
  TradePanel,
} from '../net/index.js';
import { showRewardToast } from '../net/reward-toast.js';
import { collectGold, startBattleSession, saveParty, healParty } from '../net/api.js';
import { playerLevel, CAVE_UNLOCK_LEVEL, isEventActive, EVENT_ENCOUNTER_RATE, EVENT_END_MS } from '@mn/shared';
import { hpAtLevel } from '../battle/type-chart.js';
import { totalExpNeededForLevel } from '../utils/leveling-utils.js';

/**
 * @typedef WorldSceneData
 * @type {object}
 * @property {boolean} [isPlayerKnockedOut]
 * @property {string} [area]
 * @property {boolean} [isInterior]
 * @property {import('@mn/shared').RealmId} [realm] network realm room override (decoupled from the tilemap area). Used by portals so e.g. the Badlands room can reuse the wilds tilemap.
 * @property {string} [returnArea] when inside a building: the area to drop back into on exit
 * @property {import('../types/typedef.js').Coordinate} [returnPosition] when inside a building: the world position to return to on exit
 */

export class WorldScene extends BaseScene {
  /** @type {Player} */
  #player;
  /** @type {Phaser.Tilemaps.TilemapLayer[]} */
  #encounterLayers;
  /** @type {boolean} */
  #wildMonsterEncountered;
  /** @type {Phaser.Tilemaps.ObjectLayer | undefined} */
  #signLayer;
  /** @type {DialogScene} */
  #dialogUi;
  /** @type {NPC[]} */
  #npcs;
  /** @type {NPC | undefined} */
  #npcPlayerIsInteractingWith;
  /** @type {WorldMenu} */
  #menu;
  /** @type {WorldSceneData} */
  #sceneData;
  /** @type {Item[]} */
  #items;
  /** @type {Phaser.Tilemaps.ObjectLayer | undefined} */
  #entranceLayer;
  /** @type {number} */
  #lastNpcEventHandledIndex;
  /** @type {boolean} */
  #isProcessingNpcEvent;
  /** @type {{[key: string]: Phaser.GameObjects.Zone}} */
  #eventZones;
  /** @type {{[key: string]: Phaser.GameObjects.Rectangle}} */
  #debugEventZoneObjects;
  /** @type {Phaser.Geom.Rectangle} */
  #rectangleForOverlapCheck1;
  /** @type {Phaser.Geom.Rectangle} */
  #rectangleForOverlapCheck2;
  /** @type {Phaser.Geom.Rectangle} */
  #rectangleOverlapResult;
  /** @type {Phaser.GameObjects.Graphics} */
  #gfx;
  /** @type {number | undefined} */
  #currentCutSceneId;
  /** @type {boolean} */
  #isProcessingCutSceneEvent;
  /** @type {number} */
  #lastCutSceneEventHandledIndex;
  /** @type {Phaser.GameObjects.Group} */
  #specialEncounterTileImageGameObjectGroup;
  /** @type {Phaser.Tilemaps.TilemapLayer | undefined} */
  #encounterZonePlayerIsEntering;
  /** @type {import('../types/typedef.js').CameraRegion[]} */
  #cameraRegions;
  /** @type {{ [key: string]: Phaser.Tilemaps.Tilemap}} */
  #tiledLevelMaps;
  /** @type {import('../net/remote-players.js').RemotePlayers | undefined} */
  #remotePlayers;
  /** @type {import('../net/chat-overlay.js').ChatOverlay | undefined} */
  #chatOverlay;
  /** @type {import('../net/interaction-menu.js').InteractionMenu | undefined} */
  #interactionMenu;
  /** @type {{ sessionId: string } | null} the remote player the open interaction menu is anchored to */
  #menuTarget;
  /** @type {(() => void) | undefined} */
  #netUnsubscribe;
  /** @type {import('../net/tombstones.js').Tombstones | undefined} */
  #tombstones;
  /** @type {import('../net/trade-panel.js').TradePanel | undefined} */
  #tradePanel;
  /** @type {import('@mn/shared').RealmId} the network realm room this scene is currently joined to */
  #netRealm;
  /** @type {boolean} whether the player is currently queued for an Arena match */
  #arenaQueued;
  /** @type {import('../world/roaming-mob.js').RoamingMob[]} visible roaming creatures in encounter zones */
  #mobs;
  /** @type {Phaser.Tilemaps.TilemapLayer | undefined} collision layer reference for tap-to-move pathing */
  #collisionLayer;
  /** @type {import('../common/direction.js').Direction[]} queued directions for click/tap-to-move */
  #moveQueue;
  /** @type {boolean} pointer currently held down on the world (hold/drag to steer toward cursor) */
  #pointerHeld;
  /** @type {Phaser.GameObjects.Text | undefined} username label following the local player */
  #playerLabel;
  /** @type {number} last time (ms) a dust puff kicked up under the walking player */
  #lastDustAt = 0;
  /** @type {number} last time (ms) a footstep sound played */
  #lastStepAt = 0;
  /** @type {number} player sprite x/y last frame — used to detect REAL movement (vs an idle `isMoving`) */
  #prevX = -1;
  #prevY = -1;
  /** @type {{ grp: Phaser.GameObjects.Container, x: number, y: number, value: number }[]} collectable map gold */
  #goldCoins;
  /** @type {Phaser.Time.TimerEvent | undefined} */
  #goldTimer;

  constructor() {
    super({
      key: SCENE_KEYS.WORLD_SCENE,
    });
  }

  /**
   * @param {WorldSceneData} data
   * @returns {void}
   */
  init(data) {
    super.init(data);
    this.#sceneData = data;
    this.#tiledLevelMaps = {};

    // handle when some of the fields for scene data are not populated, default to values provided, otherwise use safe defaults
    /** @type {string} */
    const area = this.#sceneData?.area || dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_LOCATION).area;
    let isInterior = this.#sceneData?.isInterior;
    if (isInterior === undefined) {
      isInterior = dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_LOCATION).isInterior;
    }
    const isPlayerKnockedOut = this.#sceneData?.isPlayerKnockedOut || false;

    this.#sceneData = {
      area,
      isInterior,
      isPlayerKnockedOut,
      // PRESERVE the interior round-trip data — without this, entering a building dropped
      // returnArea/returnPosition, so exiting fell back to main_1 and spawned the player in the void.
      returnArea: data?.returnArea,
      returnPosition: data?.returnPosition,
    };

    // update player location, and map data if the player was knocked out in a battle
    if (this.#sceneData.isPlayerKnockedOut) {
      // get the nearest knocked out spawn location from the map meta data
      let map = this.#getLevelTileMap(`${this.#sceneData.area.toUpperCase()}_LEVEL`);
      // Guard: a map without an Area-Metadata layer (e.g. an empty building shell) used to crash
      // here (`.objects[0]` on null). Fall back to the Town (main_1) — it always has a faint +
      // revive location and the free healer — so a knockout anywhere is always recoverable.
      const areaMetaDataProperties = map.getObjectLayer(OBJECT_LAYER_NAMES.AREA_METADATA)?.objects?.[0]?.properties;
      const knockOutSpawnLocation =
        (areaMetaDataProperties
          ? /** @type {import('../types/typedef.js').TiledObjectProperty[]} */ (areaMetaDataProperties).find(
              (property) => property.name === TILED_AREA_METADATA_PROPERTY.FAINT_LOCATION,
            )?.value
          : undefined) ?? 'main_1';

      // check to see if the level data we need to load is different and load that map to get player spawn data
      if (knockOutSpawnLocation !== this.#sceneData.area) {
        // Remember where we fainted so a building-interior faint can exit cleanly back outside.
        const faintSourceArea = this.#sceneData.area;
        this.#sceneData.area = knockOutSpawnLocation;
        map = this.#getLevelTileMap(`${this.#sceneData.area.toUpperCase()}_LEVEL`);

        // The faint destination can be a building interior (e.g. Town faints you into the
        // hospital, building_1). Interiors are loaded with interior bounds and use a
        // position-based round-trip, so without isInterior + returnArea/returnPosition the
        // hospital would render as a giant exterior with no working exit. Detect a building
        // destination (area key prefixed `building_`) and wire up the round-trip back outside.
        const faintsIntoBuilding = typeof knockOutSpawnLocation === 'string' && knockOutSpawnLocation.startsWith('building_');
        if (faintsIntoBuilding) {
          this.#sceneData.isInterior = true;
          this.#sceneData.returnArea = faintSourceArea;
          // Drop the player back at the building's door on the source (exterior) map.
          this.#sceneData.returnPosition = this.#getBuildingEntrancePosition(faintSourceArea, knockOutSpawnLocation);
        }
      }

      // set players spawn location to that map and finds the revive location based on that object.
      // Guard (was `.objects[0]` on a possibly-null layer): fall back to the map's player-spawn so a
      // map without a Revive-Location layer can't crash the knockout flow.
      const reviveLocation =
        map.getObjectLayer(OBJECT_LAYER_NAMES.REVIVE_LOCATION)?.objects?.[0] ??
        map.getObjectLayer(OBJECT_LAYER_NAMES.PLAYER_SPAWN_LOCATION)?.objects?.[0];
      if (reviveLocation) {
        dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_POSITION, {
          x: reviveLocation.x,
          y: reviveLocation.y - TILE_SIZE,
        });
        dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_DIRECTION, DIRECTION.UP);
      }
    }

    // During a new game flow, find the players starting location from the map data, and update
    // the data manager. Originally, this was a hard coded value, and was updated to be dynamic
    // based on our level data in Tiled.
    let isNewGame = !(dataManager.store.get(DATA_MANAGER_STORE_KEYS.GAME_STARTED) || false);
    if (isNewGame) {
      // find player spawn location and update the data manager. Use the current area's map
      // (defaults to the generated overworld) so the starting world is data-driven.
      const map = this.#getLevelTileMap(`${this.#sceneData.area.toUpperCase()}_LEVEL`);
      const playerSpawnLocationObject = map.getObjectLayer(OBJECT_LAYER_NAMES.PLAYER_SPAWN_LOCATION).objects[0];
      dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_POSITION, {
        x: playerSpawnLocationObject.x,
        y: playerSpawnLocationObject.y - TILE_SIZE,
      });
    }

    dataManager.store.set(
      DATA_MANAGER_STORE_KEYS.PLAYER_LOCATION,
      /** @type {import('../utils/data-manager.js').PlayerLocation} */ ({
        area: this.#sceneData.area,
        isInterior: this.#sceneData.isInterior,
      })
    );

    this.#wildMonsterEncountered = false;
    this.#npcPlayerIsInteractingWith = undefined;
    this.#items = [];
    this.#lastNpcEventHandledIndex = -1;
    this.#isProcessingNpcEvent = false;
    this.#encounterLayers = [];
    this.#signLayer = undefined;
    this.#entranceLayer = undefined;
    this.#eventZones = {};
    this.#debugEventZoneObjects = {};
    this.#rectangleForOverlapCheck1 = undefined;
    this.#rectangleForOverlapCheck2 = undefined;
    this.#rectangleOverlapResult = undefined;
    this.#gfx = undefined;
    this.#currentCutSceneId = undefined;
    this.#isProcessingCutSceneEvent = false;
    this.#lastCutSceneEventHandledIndex = -1;
    this.#specialEncounterTileImageGameObjectGroup = undefined;
    this.#encounterZonePlayerIsEntering = undefined;
    this.#cameraRegions = [];
    this.#arenaQueued = false;
    this.#mobs = [];
    this.#collisionLayer = undefined;
    this.#moveQueue = [];
    this.#pointerHeld = false;
    // The network realm room can be overridden by a portal (e.g. Badlands shares the wilds
    // tilemap). Falls back to the area→realm mapping when not explicitly set.
    this.#netRealm = data?.realm || realmForArea(this.#sceneData.area);
  }

  /**
   * The world's input controls. Exposed so the mobile-controls HUD scene can feed it virtual
   * joystick / A-button input without any movement changes in this scene.
   * @returns {import('../utils/controls.js').Controls}
   */
  get controls() {
    return this._controls;
  }

  /**
   * @returns {void}
   */
  create() {
    super.create();

    // Self-heal stale party stats on load (beasts caught/restored before stat-scaling existed kept
    // tiny HP, and a captured high-level beast was stuck with the template's lv-16 exp → absurd grind).
    this.#normalizePartyStats();

    // Recovery: if this account reaches the world with an EMPTY party (a battle-crash wiped the save,
    // or every monster was released), grant a fresh starter so the player is never stranded with no
    // beasts and unable to battle. New players already get a starter in profile setup, so this only
    // ever fires for an existing account that lost its team.
    this.#ensurePartyNotEmpty();

    // Server-side party AUTOSAVE (survives refresh / new device — the party used to live only in
    // localStorage and was lost on refresh). Save now (the world re-creates after every battle, so
    // this captures fresh catches + level-ups) and again every 20s. The server ignores empty parties.
    this.#autosaveParty();
    this.time.addEvent({ delay: 20000, loop: true, callback: () => this.#autosaveParty(), callbackScope: this });

    // create rectangles for checking for overlaps between game objects, added so we can recycle game objects
    this.#rectangleForOverlapCheck1 = new Phaser.Geom.Rectangle();
    this.#rectangleForOverlapCheck2 = new Phaser.Geom.Rectangle();
    this.#rectangleOverlapResult = new Phaser.Geom.Rectangle();

    // create map and collision layer
    const map = this.#getLevelTileMap(`${this.#sceneData.area.toUpperCase()}_LEVEL`);
    // The first parameter is the name of the tileset in Tiled and the second parameter is the key
    // of the tileset image used when loading the file in preload.
    const collisionTiles = map.addTilesetImage('collision', WORLD_ASSET_KEYS.WORLD_COLLISION);
    if (!collisionTiles) {
      console.log(`[${WorldScene.name}:create] encountered error while creating collision tiles from tiled`);
      return;
    }
    const collisionLayer = map.createLayer('Collision', collisionTiles, 0, 0);
    if (!collisionLayer) {
      console.log(`[${WorldScene.name}:create] encountered error while creating collision layer using data from tiled`);
      return;
    }
    collisionLayer.setAlpha(TILED_COLLISION_LAYER_ALPHA).setDepth(2);
    // keep a reference for tap/click-to-move pathfinding on the grid
    this.#collisionLayer = collisionLayer;

    // create interactive layer
    const hasSignLayer = map.getObjectLayer(OBJECT_LAYER_NAMES.SIGN) !== null;
    if (hasSignLayer) {
      this.#signLayer = map.getObjectLayer(OBJECT_LAYER_NAMES.SIGN);
    }

    // create layer for scene transitions entrances
    const hasSceneTransitionLayer = map.getObjectLayer(OBJECT_LAYER_NAMES.SCENE_TRANSITIONS) !== null;
    if (hasSceneTransitionLayer) {
      this.#entranceLayer = map.getObjectLayer(OBJECT_LAYER_NAMES.SCENE_TRANSITIONS);
    }

    // create collision layers for encounters
    this.#createEncounterAreas(map);

    // Bounds = the ACTUAL map size + a cover-fit zoom so the world ALWAYS fills the screen with no
    // black void — for the big overworld, the narrow 40-wide Town, AND building interiors (which
    // previously had NO bounds and a fixed 0.8 zoom, so the small 832x576 room floated in black and
    // the camera drifted off it). (Was hardcoded to 2560x5184, breaking both narrow + wide maps.)
    this.cameras.main.setBounds(0, 0, map.widthInPixels, map.heightInPixels);
    this.#worldMapPixelW = map.widthInPixels;
    this.#worldMapPixelH = map.heightInPixels;
    // Themed backdrop so the camera NEVER clears to pure black at a map edge (the "black void on the
    // side" bug). The cover-fit below should fill the viewport, but on odd viewport ratios / a resize
    // that lands mid-transition this is a bulletproof safety net that reads as grass, not a void.
    this.cameras.main.setBackgroundColor(this.#backdropColor());
    this.#fitCamera();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.#fitCamera, this);
    // Re-fit once after layout settles — `this.scale.width` can be stale on the first create() tick
    // right after a teleport, which is exactly when players saw the void appear.
    this.time.delayedCall(60, () => {
      if (this.sys?.isActive?.()) this.#fitCamera();
    });
    // Stretch the background to the map's pixel size. The overworld ground ships DOWNSCALED (the full
    // 6144² texture OOM'd / hit the texture-size limit on mobile GPUs → black screen + "Script error"),
    // so it's scaled up to fill the world here. Town/cave backgrounds already match 1:1 (no-op).
    this.add
      .image(0, 0, `${this.#sceneData.area.toUpperCase()}_BACKGROUND`, 0)
      .setOrigin(0)
      .setDisplaySize(map.widthInPixels, map.heightInPixels);

    // create items and collisions
    this.#createItems(map);

    // create low-poly decor props (rendered before the player so the player walks in front)
    this.#createDecor(map);

    // create npcs
    this.#createNPCs(map);

    // create player and have camera focus on the player
    this.#player = new Player({
      scene: this,
      position: dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_POSITION),
      direction: dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_DIRECTION),
      collisionLayer: collisionLayer,
      spriteGridMovementFinishedCallback: () => {
        this.#handlePlayerMovementUpdate();
      },
      spriteChangedDirectionCallback: () => {
        this.#handlePlayerDirectionUpdate();
      },
      otherCharactersToCheckForCollisionsWith: this.#npcs,
      objectsToCheckForCollisionsWith: this.#items,
      entranceLayer: this.#entranceLayer,
      enterEntranceCallback: (entranceName, entranceId, isBuildingEntrance) => {
        this.#handleEntranceEnteredCallback(entranceName, entranceId, isBuildingEntrance);
      },
      spriteGridMovementStartedCallback: (position) => {
        this.#handlePlayerMovementStarted(position);
      },
    });
    // Smooth follow: ease the camera toward the player (lerp) instead of snapping every frame, so
    // movement feels less rigid. roundPixels stays on for crisp tiles.
    this.cameras.main.startFollow(this.#player.sprite, true, 0.15, 0.15);
    this.input.addPointer(1); // enable a 2nd pointer so pinch-to-zoom works on touch
    this.#setupZoomControls();

    // Name label above the LOCAL player (remote players already get one) so usernames are visible
    // in-world — players can recognize each other.
    const myName = dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_NAME) || 'You';
    this.#playerLabel = this.add
      .text(this.#player.sprite.x, this.#player.sprite.y - 18, myName, {
        fontFamily: 'Kenney-Future-Narrow, system-ui, sans-serif',
        fontSize: '15px',
        color: '#aef0c4',
        stroke: '#06140d',
        strokeThickness: 4,
      })
      .setOrigin(0.5, 1)
      .setDepth(3000);

    // update camera bounds for the given level
    this.#cameraRegions = TiledUtils.createCameraRegions(map);
    if (CameraUtils.updateMainCameraBounds(this, this.#player.sprite, this.#cameraRegions)) {
      // Bounds shrank/grew to a new camera region → refit the zoom so the viewport stays filled.
      this.#fitCamera();
    }

    // update our collisions with npcs
    this.#npcs.forEach((npc) => {
      npc.addCharacterToCheckForCollisionsWith(this.#player);
    });

    // create foreground for depth. Explicit depth 5 so it sits ABOVE every walking sprite (the local
    // player + remote players are depth 0–1) — without it the foreground defaulted to depth 0 and the
    // remote players (depth 1) rendered ON TOP of trees/roofs, i.e. "floating" (reported bug).
    this.add.image(0, 0, `${this.#sceneData.area.toUpperCase()}_FOREGROUND`, 0).setOrigin(0).setDepth(5);

    // living-world ambience: birds gliding over the view, wind motes drifting, dust under footsteps
    this.#createWorldAmbience();

    // create menu
    this.#menu = new WorldMenu(this);

    // create event zones
    this.#createEventEncounterZones(map);

    // spawn the visible roaming creatures inside the encounter zones (mouse/keyboard primary
    // encounter source). Safe no-op when the area has no encounter layers.
    this.#spawnRoamingMobs(map);

    // Map gold: occasionally drop a collectable coin near the player (overworld only, not interiors).
    this.#goldCoins = [];
    if (!this.#sceneData.isInterior) {
      this.#goldTimer = this.time.addEvent({
        delay: isEventActive() ? 11000 : 30000, // coins drop ~3× faster during the special event
        loop: true,
        callback: () => this.#spawnGoldCoin(),
        callbackScope: this,
      });
      this.time.delayedCall(4000, () => this.#spawnGoldCoin()); // one early so players see gold fast
    }

    // wire up mouse/touch: tap-to-move on the world + clickable NPCs / signs / mobs
    this.#setupPointerInput();
    this.#makeNpcsClickable();
    this.#makeSignsClickable(map);

    if (ENABLE_ZONE_DEBUGGING) {
      // used for debugging the overlaps for event zones
      this.#gfx = this.add.graphics({ lineStyle: { width: 4, color: 0x00ffff } });
    }

    // Knockout recovery must NOT depend on the camera fade firing: a missed fade would otherwise
    // return the player with a 0-HP party AND movement locked. Heal + unlock UNCONDITIONALLY here
    // (#healPlayerParty is idempotent — sets currentHp=maxHp); the dialog below is purely cosmetic.
    if (this.#sceneData.isPlayerKnockedOut) {
      this.#healPlayerParty();
      this._controls.lockInput = false;
    }

    this.cameras.main.fadeIn(1000, 0, 0, 0, (camera, progress) => {
      if (progress === 1) {
        // Brief "your team was healed" message + auto-close so a player who doesn't press SPACE is
        // never left waiting (movement is already freed above).
        if (this.#sceneData.isPlayerKnockedOut) {
          this.#dialogUi.showDialogModal([
            'Your team was defeated — but they have been healed up. Get back out there!',
          ]);
          this.time.delayedCall(3500, () => {
            if (this.#dialogUi?.isVisible) this.#dialogUi.hideDialogModal();
          });
        }

        // Evolutions trigger OUTSIDE battle. The world scene re-creates whenever the player
        // returns from a battle (or enters the world), so this fires after every battle once the
        // party is loaded. Levelled-up monsters that crossed an evolution threshold morph here and
        // the celebration overlay plays over the world. Never touches the battle scene/state.
        this.#runEvolutionCheck();
      }
    });
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.GAME_STARTED, true);

    // add audio
    playBackgroundMusic(this, AUDIO_ASSET_KEYS.MAIN);
    // add UI scene for cutscene and dialog
    this.scene.launch(SCENE_KEYS.CUTSCENE_SCENE);
    this.scene.launch(SCENE_KEYS.DIALOG_SCENE);
    // always-visible HUD overlays (currency + player card/menu/party/quest). They MUST render
    // ABOVE the world: the world's map background is opaque + full-screen, so any HUD scene sitting
    // below it in the scene render order is completely covered — which is exactly why the HUD was
    // invisible. Force a fresh launch and bring each to the TOP of the scene render order.
    [SCENE_KEYS.CURRENCY_HUD_SCENE, SCENE_KEYS.WORLD_HUD_SCENE].forEach((hudKey) => {
      if (this.scene.isActive(hudKey)) {
        this.scene.stop(hudKey);
      }
      this.scene.launch(hudKey);
      this.scene.bringToTop(hudKey);
    });
    // Limited-time SPECIAL EVENT banner (top-center; auto-removes when the event window closes).
    if (isEventActive() && !this.#sceneData?.isInterior) {
      const banner = this.add
        .text(this.scale.width / 2, 10, '⚡ SPECIAL EVENT LIVE  —  2× GOLD · MORE SPAWNS! ⚡', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          fontStyle: 'bold',
          color: '#eafff5',
          backgroundColor: '#0c7a4dcc',
          padding: { x: 16, y: 7 },
        })
        .setOrigin(0.5, 0)
        .setScrollFactor(0)
        .setDepth(9999);
      this.tweens.add({ targets: banner, alpha: 0.6, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      this.time.delayedCall(Math.max(1000, EVENT_END_MS - Date.now()), () => banner.destroy());
    }
    // On-screen touch controls (virtual joystick + A button). Shown on touch/coarse-pointer devices
    // AND on phone-sized viewports — so it appears on real phones, in DevTools device/responsive
    // mode (testable), and on narrow windows. Re-evaluated on RESIZE so rotating or toggling device
    // emulation shows/hides it live without a reload. Feeds this scene's controls (movement unchanged).
    // force=true on create so a world→world restart always re-launches the controls (see method doc);
    // RESIZE re-evaluates without forcing (won't interrupt an in-progress joystick drag).
    this.#updateMobileControls(true);
    this.scale.on(Phaser.Scale.Events.RESIZE, this.#onMobileControlsResize, this);

    this.#dialogUi = /** @type {DialogScene} */ (this.scene.get(SCENE_KEYS.DIALOG_SCENE));
    this.#specialEncounterTileImageGameObjectGroup = this.add.group({ classType: Phaser.GameObjects.Image });

    // wire up multiplayer (resilient: no-ops if the realtime server is offline / unconfigured)
    this.#setupMultiplayer();

    // First-run onboarding: show the "How to Play" overlay ONCE to brand-new players on their first
    // outdoor world entry. Gated on a persisted flag so it never reappears, skipped on interior
    // re-entries (e.g. returning from a building/battle), and fully wrapped so a failure here can
    // NEVER block world entry. Purely additive — no gameplay state is touched.
    try {
      const seen = dataManager.store.get(DATA_MANAGER_STORE_KEYS.HOW_TO_PLAY_SEEN) === true;
      if (!seen && !this.#sceneData?.isInterior) {
        this.openHowToPlay();
      }
    } catch (e) {
      console.warn('[WorldScene] how-to-play onboarding launch skipped', e);
    }

    // Guest / spectator nudge: with no connected wallet the session is local-only (nothing persists),
    // so a subtle top banner invites them to connect. Purely additive + guarded — never blocks entry.
    try {
      if (!netManager.wallet && !this.#sceneData?.isInterior) {
        this.add
          .text(this.scale.width / 2, 92, '👀 Spectating — connect a wallet to save progress', {
            fontFamily: 'Kenney-Future-Narrow, system-ui, sans-serif',
            fontSize: '14px',
            color: '#ffe28a',
            backgroundColor: '#0b1a12d9',
            padding: { x: 12, y: 6 },
            align: 'center',
            wordWrap: { width: Math.min(420, this.scale.width - 40) },
          })
          .setOrigin(0.5, 0)
          .setScrollFactor(0)
          .setDepth(2000);
      }
    } catch (e) {
      console.warn('[WorldScene] guest banner skipped', e);
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.#teardownMultiplayer();
      this.#goldTimer?.remove(false);
      this.#goldCoins?.forEach((c) => c.grp.destroy());
      this.#goldCoins = [];
      // tear down the currency HUD with the world (e.g. when starting a battle); it is
      // re-launched on the next world-scene create. Portal transitions restart the world.
      if (this.scene.isActive(SCENE_KEYS.CURRENCY_HUD_SCENE)) {
        this.scene.stop(SCENE_KEYS.CURRENCY_HUD_SCENE);
      }
      if (this.scene.isActive(SCENE_KEYS.WORLD_HUD_SCENE)) {
        this.scene.stop(SCENE_KEYS.WORLD_HUD_SCENE);
      }
      if (this.scene.isActive(SCENE_KEYS.MOBILE_CONTROLS_SCENE)) {
        this.scene.stop(SCENE_KEYS.MOBILE_CONTROLS_SCENE);
      }
      this.scale.off(Phaser.Scale.Events.RESIZE, this.#onMobileControlsResize, this);
      this.scale.off(Phaser.Scale.Events.RESIZE, this.#fitCamera, this);
    });
  }

  /**
   * Connects the world scene to the realtime server: joins the realm room for the current
   * area, renders other players, and shows a chat overlay. Everything degrades gracefully
   * when offline so single-player is unaffected.
   * @returns {void}
   */
  #setupMultiplayer() {
    const client = netManager.ensureClient();
    this.#remotePlayers = new RemotePlayers(this, 1, (x, y) => this.#snapRemoteToPath(x, y));
    this.#tombstones = new Tombstones(this);
    this.#chatOverlay = new ChatOverlay({
      onSend: (text) => client.sendChat(text),
    });
    // Player-to-player interaction menu (Message / Heart / Challenge) + its modals/toasts.
    this.#interactionMenu = new InteractionMenu({
      onWhisper: (targetSessionId, text) => client.whisper(targetSessionId, text),
      onHeart: (targetSessionId) => client.heart(targetSessionId),
      onChallenge: (targetSessionId, wager) => {
        client.challenge(targetSessionId, wager);
        this.#menuTarget = { sessionId: targetSessionId };
      },
      onChallengeResponse: (fromSessionId, accept) => client.challengeResponse(fromSessionId, accept),
      getGold: () => dataManager.store.get(DATA_MANAGER_STORE_KEYS.GOLD) || 0,
    });
    // Touch / click on a remote sprite opens the interaction menu anchored to that player.
    this.#remotePlayers.onTap((ref) => this.#openInteractionMenu(ref.sessionId, ref.name, ref.sprite));
    this.#tradePanel = new TradePanel({
      onOffer: (gold) => client.tradeOffer(gold, [], []),
      onConfirm: () => client.tradeConfirm(),
      onCancel: () => client.tradeCancel(),
      onAccept: () => {
        // accepting an incoming request: the partner already sent tradeRequest, so we are
        // implicitly in the trade; nothing extra to send until we offer/confirm.
      },
    });

    // listen for network events
    this.#netUnsubscribe = client.on((event) => {
      switch (event.type) {
        case 'status':
          this.#chatOverlay?.addSystem(event.online ? 'Connected to realm.' : 'Offline — single-player.');
          if (!event.online) {
            this.#remotePlayers?.clear();
            this.#tombstones?.clear();
          }
          break;
        case 'playerJoin':
        case 'playerUpdate':
          this.#remotePlayers?.upsert(event.player);
          break;
        case 'playerLeave':
          this.#remotePlayers?.remove(event.sessionId);
          break;
        case 'chat':
          this.#chatOverlay?.addMessage(event.from, event.text);
          break;
        case 'system':
          this.#chatOverlay?.addSystem(event.text);
          break;
        case 'tombstones':
          // reconcile rendered tombstone markers; placed near the local player since the
          // room state only replicates ids (no authoritative positions).
          this.#tombstones?.sync(event.ids, { x: this.#player.sprite.x, y: this.#player.sprite.y });
          break;
        case 'server':
          this.#handleServerMessage(event.message);
          break;
        default:
          break;
      }
    });

    // join the room for this scene's network realm (portal override or area-derived)
    const spawn = { x: this.#player.sprite.x, y: this.#player.sprite.y };
    const dir = toNetDir(this.#player.direction);
    void client.joinRealm(this.#netRealm, spawn, dir);

    // brief "Entering <Realm>" banner on (re)joining a realm
    this.#showRealmBanner(this.#netRealm);
  }

  /**
   * Handle a typed ServerMessage relayed from the realtime server. Each branch degrades
   * gracefully and never throws into the net listener.
   * @param {import('@mn/shared').ServerMessage} message
   * @returns {void}
   */
  #handleServerMessage(message) {
    switch (message.t) {
      case 'reward':
        // a CollectorCrypt card was won — show the celebratory "you won a real card!" toast.
        // Display-only: the NFT is airdropped on-chain by apps/rewards regardless.
        showRewardToast(this, message.cardName, message.rarity);
        this.#chatOverlay?.addSystem(`⭐ You won a real card: ${message.cardName} (${message.rarity})!`);
        break;
      case 'battleStart':
        // PvP (Badlands duel) or Arena pairing — open the server-synced PvP scene.
        this.#arenaQueued = false;
        this.#startPvpBattle(message);
        break;
      case 'died':
        // We lost a Badlands duel: a tombstone was dropped at our position.
        this.#tombstones?.place(message.tombstoneId, { x: this.#player.sprite.x, y: this.#player.sprite.y });
        this.#dialogUi?.showDialogModal([
          'You were defeated in the Badlands!',
          'Your loot dropped into a tombstone where you fell.',
        ]);
        this.#chatOverlay?.addSystem('💀 You were defeated — loot dropped into a tombstone.');
        break;
      case 'tradeRequested':
        this.#tradePanel?.openIncoming(message.fromSessionId, message.fromName);
        this.#chatOverlay?.addSystem(`🤝 ${message.fromName} wants to trade (Q to respond).`);
        break;
      case 'tradeUpdate':
        this.#tradePanel?.setPartnerOffer(message.theirGold, message.theirItemIds, message.theirMonsterIds);
        break;
      case 'tradeCompleted':
        this.#tradePanel?.setStatus('Trade completed!');
        this.#chatOverlay?.addSystem('✅ Trade completed.');
        this.time.delayedCall(1200, () => this.#tradePanel?.hide());
        break;
      // ── Player-to-player social relays ──
      case 'whisper':
        // a received private message → highlighted in the chat overlay (peeks open on mobile)
        this.#chatOverlay?.addWhisper(message.fromUsername, message.text);
        break;
      case 'heart':
        // float a ❤️ above the SENDER's sprite + a small toast (no-op if they've left)
        this.#floatHeart(message.fromSessionId);
        this.#interactionMenu?.toast(`❤️ from ${message.fromUsername}`, '#f87171');
        break;
      case 'challengeReceived':
        // incoming 1v1 → Accept/Decline modal; accepting triggers the server's battleStart flow
        this.#interactionMenu?.showIncomingChallenge(message.fromSessionId, message.fromUsername, message.wager);
        break;
      case 'challengeExpired':
        // our pending challenge lapsed, OR an incoming one we were shown timed out
        this.#interactionMenu?.dismissIncomingChallenge();
        this.#interactionMenu?.close();
        this.#menuTarget = null;
        this.#interactionMenu?.toast('Challenge expired.', '#b9b9c9');
        break;
      case 'challengeDeclined':
        this.#interactionMenu?.close();
        this.#menuTarget = null;
        this.#interactionMenu?.toast('Challenge declined.', '#b9b9c9');
        break;
      case 'challengeCancelled':
        this.#interactionMenu?.dismissIncomingChallenge();
        this.#interactionMenu?.close();
        this.#menuTarget = null;
        this.#interactionMenu?.toast(
          message.reason === 'insufficient_gold' ? 'Not enough Gold for that wager.' : 'Challenge cancelled.',
          '#f87171',
        );
        break;
      case 'duelResult': {
        // wager settlement banner: won → pocketed `wagerWon` (the 2× pot minus stake), lost → 0.
        const mySession = netManager.client?.sessionId ?? null;
        const iWon = message.winnerSessionId != null && message.winnerSessionId === mySession;
        if (iWon) {
          // wagerWon = the pot the winner pocketed (2× the stake). Show it directly.
          this.#interactionMenu?.banner(`You won ${message.wagerWon} Gold!`, '#fbbf24');
        } else if (message.winnerSessionId == null) {
          this.#interactionMenu?.banner('Duel drawn — wager refunded.', '#b9b9c9');
        } else {
          // The loser forfeits their stake (half the winner's pot). wagerWon is winner-centric.
          const lost = Math.max(0, Math.floor(message.wagerWon / 2));
          this.#interactionMenu?.banner(`You lost ${lost} Gold.`, '#f87171');
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * Float a ❤️ emoji rising + fading above a remote player's sprite. No-op if that player's
   * sprite is gone (left / out of view).
   * @param {string} sessionId
   * @returns {void}
   */
  #floatHeart(sessionId) {
    const ref = this.#remotePlayers?.ref(sessionId);
    if (!ref?.sprite?.active) return;
    const heart = this.add
      .text(ref.sprite.x, ref.sprite.y - 24, '❤️', { fontSize: '24px' })
      .setOrigin(0.5, 1)
      .setDepth(2000);
    this.tweens.add({
      targets: heart,
      y: heart.y - 48,
      alpha: 0,
      duration: 1400,
      ease: 'Sine.easeOut',
      onComplete: () => heart.destroy(),
    });
  }

  /**
   * Brief, non-blocking "Entering <Realm>" transition banner. Skips Town on first spawn so it
   * only fires on actual realm transitions feels meaningful, but harmless if shown.
   * @param {import('@mn/shared').RealmId} realm
   * @returns {void}
   */
  #showRealmBanner(realm) {
    const label = REALM_LABELS[realm] ?? realm;
    const text = this.add
      .text(this.scale.width / 2, this.scale.height / 2, `Entering ${label}`, {
        fontFamily: 'Kenney-Future-Narrow, system-ui, sans-serif',
        fontSize: '40px',
        color: '#ffffff',
        backgroundColor: '#000000a0',
        padding: { x: 24, y: 14 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(2500)
      .setAlpha(0);
    this.tweens.add({ targets: text, alpha: 1, duration: 250, yoyo: true, hold: 900, onComplete: () => text.destroy() });
  }

  /**
   * @returns {void}
   */
  #teardownMultiplayer() {
    this.#netUnsubscribe?.();
    this.#netUnsubscribe = undefined;
    this.#remotePlayers?.clear();
    this.#remotePlayers = undefined;
    this.#tombstones?.clear();
    this.#tombstones = undefined;
    this.#chatOverlay?.destroy();
    this.#chatOverlay = undefined;
    this.#interactionMenu?.destroy();
    this.#interactionMenu = undefined;
    this.#menuTarget = null;
    this.#tradePanel?.destroy();
    this.#tradePanel = undefined;
    // leave the room so the next world scene re-joins cleanly with fresh spawn coords
    void netManager.client?.leave();
  }

  /**
   * @param {DOMHighResTimeStamp} time
   * @returns {void}
   */
  /**
   * Living-overworld ambience — pure procedural (no art), cheap, and looping forever:
   *  • birds: dark V silhouettes that flap + glide across the viewport (screen-space, above the foreground),
   *  • wind motes: soft pollen/leaf specks drifting on the breeze (screen-space),
   *  • dust: kicked up under the player's feet while walking (see #kickDust, called from update()).
   * Skipped inside buildings (no sky there).
   * @returns {void}
   */
  #createWorldAmbience() {
    if (this.#sceneData.isInterior) return;
    const rand = (a, b) => a + Math.random() * (b - a);

    // birds gliding across the sky
    for (let i = 0; i < 3; i += 1) {
      const g = this.add.graphics().setScrollFactor(0).setDepth(6).setAlpha(0.4);
      let flap = false;
      const draw = () => {
        g.clear();
        g.lineStyle(2, 0x2b2b3a, 0.7);
        const w = 7;
        const d = flap ? 2 : 6;
        g.beginPath();
        g.moveTo(-w, 0);
        g.lineTo(0, -d);
        g.lineTo(w, 0);
        g.strokePath();
      };
      draw();
      this.time.addEvent({ delay: 300, loop: true, callback: () => { flap = !flap; draw(); } });
      const fly = () => {
        const H = this.scale.height;
        g.setPosition(-30, rand(40, Math.max(90, H * 0.4)));
        this.tweens.add({
          targets: g,
          x: this.scale.width + 40,
          y: `+=${rand(-20, 20)}`,
          duration: rand(9000, 15000),
          delay: i === 0 ? rand(0, 3000) : rand(0, 8000),
          onComplete: fly,
        });
      };
      fly();
    }

    // wind motes drifting horizontally on the breeze
    for (let i = 0; i < 14; i += 1) {
      const m = this.add
        .circle(rand(0, this.scale.width), rand(0, this.scale.height), rand(1, 2.2), 0xf3f0c8, 0.45)
        .setScrollFactor(0)
        .setDepth(6);
      const drift = () => {
        this.tweens.add({
          targets: m,
          x: `+=${rand(60, 180)}`,
          y: `+=${rand(-30, 40)}`,
          alpha: 0,
          duration: rand(5000, 9000),
          onComplete: () => {
            m.setPosition(rand(-20, this.scale.width * 0.4), rand(0, this.scale.height));
            m.setAlpha(rand(0.25, 0.5));
            drift();
          },
        });
      };
      drift();
    }
  }

  /**
   * Kick a small puff of dust up at a world position (the player's feet) — a few soft tan specks that
   * drift up + out, fade, and self-destroy. Cheap and ephemeral; called while the player walks.
   * @param {number} x
   * @param {number} y
   * @returns {void}
   */
  #kickDust(x, y) {
    const rand = (a, b) => a + Math.random() * (b - a);
    for (let i = 0; i < 3; i += 1) {
      const d = this.add.circle(x + rand(-5, 5), y + rand(-2, 2), rand(2, 3.5), 0xcdb38a, 0.5).setDepth(0);
      this.tweens.add({
        targets: d,
        x: d.x + rand(-10, 10),
        y: d.y - rand(4, 12),
        scale: 0.2,
        alpha: 0,
        duration: rand(380, 560),
        onComplete: () => d.destroy(),
      });
    }
  }

  /**
   * Sample the ground colour directly under the player from the area's background texture to choose
   * the footstep surface: warm tan (path / plaza / cave floor) = sand, green = grass. Cheap — Phaser
   * getPixel samples a single pixel. Defaults to grass on any miss.
   * @returns {boolean} true if the player is on sandy/dirt ground
   */
  #surfaceUnderPlayerIsSand() {
    try {
      const key = `${this.#sceneData.area.toUpperCase()}_BACKGROUND`;
      if (!this.textures.exists(key)) return false;
      const px = Math.round(this.#player.sprite.x + TILE_SIZE / 2);
      const py = Math.round(this.#player.sprite.y + TILE_SIZE * 0.6);
      const c = this.textures.getPixel(px, py, key);
      if (!c) return false;
      // grass reads green-dominant; sand / dirt / cave-floor reads warm (red ≥ green).
      return c.red >= c.green + 4;
    } catch {
      return false;
    }
  }

  update(time) {
    super.update(time);

    if (this.#wildMonsterEncountered) {
      this.#player.update(time);
      return;
    }

    const wasSpaceKeyPressed = this._controls.wasSpaceKeyPressed();
    const selectedDirectionHeldDown = this._controls.getDirectionKeyPressedDown();
    const selectedDirectionPressedOnce = this._controls.getDirectionKeyJustPressed();
    const isShiftKeyDown = this._controls.isShiftKeyDown();

    if (this.#isProcessingCutSceneEvent) {
      this.#player.update(time);
      this.#npcs.forEach((npc) => {
        npc.update(time);
      });
      if (wasSpaceKeyPressed && this.#npcPlayerIsInteractingWith) {
        this.#handlePlayerInteraction();
      }
      return;
    }

    if (selectedDirectionHeldDown !== DIRECTION.NONE && !this.#isPlayerInputLocked()) {
      // keyboard always wins and cancels any queued tap-to-move path
      this.#moveQueue = [];
      this.#player.moveCharacter(selectedDirectionHeldDown, isShiftKeyDown);
    } else if (
      this.#moveQueue.length > 0 &&
      !this.#player.isMoving &&
      !this.#isPlayerInputLocked()
    ) {
      // consume the next queued tap-to-move step
      const nextDir = this.#moveQueue.shift();
      this.#player.moveCharacter(nextDir, false);
    }

    // update the visible roaming mobs (idle wander); cheap, runs only when not in an encounter
    this.#mobs.forEach((mob) => mob.update(time));

    // pick up any gold coin the player is standing on
    this.#collectNearbyGold();

    // keep the local player's username label following their sprite
    if (this.#playerLabel && this.#player) {
      this.#playerLabel.setPosition(this.#player.sprite.x + TILE_SIZE / 2, this.#player.sprite.y - 24);
    }

    // Detect REAL movement by the sprite's position actually changing this frame — `isMoving` can stay
    // truthy while idle (queued/held-key state), which made footsteps + dust keep firing while standing
    // still (reported bug). Position delta is unambiguous: no movement → no sound, no dust.
    const curX = this.#player?.sprite.x ?? 0;
    const curY = this.#player?.sprite.y ?? 0;
    const movedNow = Math.abs(curX - this.#prevX) > 0.5 || Math.abs(curY - this.#prevY) > 0.5;
    this.#prevX = curX;
    this.#prevY = curY;

    if (!this.#sceneData.isInterior && movedNow) {
      // dust kicks up under the player's feet while walking (throttled), so the ground feels alive
      if (time - this.#lastDustAt > 170) {
        this.#lastDustAt = time;
        this.#kickDust(curX + TILE_SIZE / 2, curY + TILE_SIZE * 0.85);
      }
      // surface-aware walking footstep (quiet): sample the ground colour to pick sand vs grass
      if (time - this.#lastStepAt > 330) {
        this.#lastStepAt = time;
        const onSand = this.#surfaceUnderPlayerIsSand();
        playFootstepFx(this, onSand ? AUDIO_ASSET_KEYS.STEP_SAND : AUDIO_ASSET_KEYS.STEP_GRASS);
      }
    }

    if (wasSpaceKeyPressed && !this.#player.isMoving && !this.#menu.isVisible) {
      this.#handlePlayerInteraction();
    }

    // keep the interaction menu anchored to its target; close it if the player moves or the
    // target vanished (a no-op when nothing is open).
    if (this.#interactionMenu?.isOpen && this.#menuTarget) {
      const ref = this.#remotePlayers?.ref(this.#menuTarget.sessionId);
      if (!ref || this.#player.isMoving || this.#moveQueue.length > 0) {
        this.#interactionMenu.close();
        this.#menuTarget = null;
      } else {
        const anchor = this.#worldToPage(ref.sprite.x, ref.sprite.y - 28);
        this.#interactionMenu.trackAnchor(anchor.x, anchor.y);
      }
    }

    // while typing in the chat overlay, the trade panel, or the interaction menu's inputs, the
    // game must not react to keys.
    if (this.#chatOverlay?.isTyping || this.#interactionMenu?.isTyping) {
      this.#player.update(time);
      this.#npcs.forEach((npc) => npc.update(time));
      return;
    }

    // multiplayer "interact with a nearby player" (Q): open the interaction menu.
    if (this._controls.wasQKeyPressed() && !this.#menu.isVisible && !this.#dialogUi.isVisible) {
      this.#handleNearbyPlayerInteraction();
    }

    if (this._controls.wasEnterKeyPressed() && !this.#player.isMoving) {
      if (this.#dialogUi.isVisible || this.#isProcessingNpcEvent) {
        return;
      }

      if (this.#menu.isVisible) {
        this.#menu.hide();
        return;
      }

      this.#menu.show();
    }

    if (this.#menu.isVisible) {
      if (selectedDirectionPressedOnce !== DIRECTION.NONE) {
        this.#menu.handlePlayerInput(selectedDirectionPressedOnce);
      }

      if (wasSpaceKeyPressed) {
        this.#menu.handlePlayerInput('OK');

        if (this.#menu.selectedMenuOption === 'SAVE') {
          this.#menu.hide();
          dataManager.saveData();
          this.#dialogUi.showDialogModal(['Game progress has been saved']);
        }

        if (this.#menu.selectedMenuOption === 'MONSTERS') {
          // openParty() guards an empty party itself; hide the menu either way
          this.#menu.hide();
          this.openParty();
        }

        if (this.#menu.selectedMenuOption === 'BAG') {
          this.#menu.hide();
          this.openInventory();
        }

        if (this.#menu.selectedMenuOption === 'QUESTS') {
          this.#menu.hide();
          this.openQuests();
        }

        if (this.#menu.selectedMenuOption === 'CARDSHOP') {
          this.#menu.hide();
          this.openCardShop();
        }

        if (this.#menu.selectedMenuOption === 'MARKET') {
          this.#menu.hide();
          this.openMarket();
        }

        if (this.#menu.selectedMenuOption === 'MAP') {
          this.#menu.hide();
          this.openMap();
        }

        if (this.#menu.selectedMenuOption === 'ARENA') {
          this.#menu.hide();
          this.#handleArenaQueue();
        }

        if (this.#menu.selectedMenuOption === 'PROFILE') {
          this.#menu.hide();
          this.openProfile();
        }

        if (this.#menu.selectedMenuOption === 'SETTINGS') {
          this.#menu.hide();
          this.openSettings();
        }

        if (this.#menu.selectedMenuOption === 'EXIT') {
          this.#menu.hide();
        }
      }

      if (this._controls.wasBackKeyPressed()) {
        this.#menu.hide();
      }
    }

    this.#player.update(time);

    this.#npcs.forEach((npc) => {
      npc.update(time);
    });
  }

  /**
   * @returns {void}
   */
  #handlePlayerInteraction() {
    if (this.#dialogUi.isAnimationPlaying) {
      // Still scrolling → reveal the whole line instantly instead of ignoring the press (a tap to
      // skip the typewriter). The next press then advances.
      this.#dialogUi.completeCurrentMessage();
      return;
    }

    if (this.#dialogUi.isVisible && !this.#dialogUi.moreMessagesToShow) {
      this.#dialogUi.hideDialogModal();
      if (this.#currentCutSceneId !== undefined) {
        this.#isProcessingCutSceneEvent = false;
        this.#handleCutSceneInteraction();
      }
      if (this.#npcPlayerIsInteractingWith) {
        this.#handleNpcInteraction();
      }
      return;
    }

    if (this.#dialogUi.isVisible && this.#dialogUi.moreMessagesToShow) {
      this.#dialogUi.showNextMessage();
      return;
    }

    // check for a nearby tombstone to loot (Badlands death drops). Server validates the loot.
    const nearbyTombstone = this.#tombstones?.nearest(this.#player.sprite.x, this.#player.sprite.y);
    if (nearbyTombstone) {
      netManager.client?.lootTombstone(nearbyTombstone.id);
      this.#dialogUi.showDialogModal(['You search the tombstone…']);
      return;
    }

    // get players current direction and check 1 tile over in that direction to see if there is an object that can be interacted with
    const { x, y } = this.#player.sprite;
    const targetPosition = getTargetPositionFromGameObjectPositionAndDirection({ x, y }, this.#player.direction);

    // check for sign, and display appropriate message if player is not facing up
    const nearbySign = this.#signLayer?.objects.find((object) => {
      if (!object.x || !object.y) {
        return false;
      }

      // In Tiled, the x value is how far the object starts from the left, and the y is the bottom of tiled object that is being added
      return object.x === targetPosition.x && object.y - TILE_SIZE === targetPosition.y;
    });

    if (nearbySign) {
      /** @type {import('../types/typedef.js').TiledObjectProperty[]} */
      const props = nearbySign.properties;
      /** @type {number} */
      const signId = props.find((prop) => prop.name === TILED_SIGN_PROPERTY.ID)?.value;
      const sign = DataUtils.getSignData(this, signId);

      const usePlaceholderText = this.#player.direction !== DIRECTION.UP;
      let textToShow = CANNOT_READ_SIGN_TEXT;
      if (!usePlaceholderText) {
        textToShow = sign?.message || SAMPLE_TEXT;
      }
      this.#dialogUi.showDialogModal([textToShow]);
      return;
    }

    const nearbyNpc = this.#npcs.find((npc) => {
      return npc.sprite.x === targetPosition.x && npc.sprite.y === targetPosition.y;
    });
    if (nearbyNpc) {
      nearbyNpc.facePlayer(this.#player.direction);
      nearbyNpc.isTalkingToPlayer = true;
      this.#npcPlayerIsInteractingWith = nearbyNpc;
      this.#handleNpcInteraction();
      return;
    }

    // check for a nearby item and display message about player finding the item
    let nearbyItemIndex;
    const nearbyItem = this.#items.find((item, index) => {
      if (item.position.x === targetPosition.x && item.position.y === targetPosition.y) {
        nearbyItemIndex = index;
        return true;
      }
      return false;
    });
    if (nearbyItem) {
      // add item to inventory and display message to player
      const item = DataUtils.getItem(this, nearbyItem.itemId);
      dataManager.addItem(item, 1);
      nearbyItem.gameObject.destroy();
      this.#items.splice(nearbyItemIndex, 1);
      dataManager.addItemPickedUp(nearbyItem.id);
      this.#dialogUi.showDialogModal([`You found a ${item.name}`]);
    }
  }

  /**
   * @returns {void}
   */
  #handlePlayerMovementUpdate() {
    // update player position on global data store
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_POSITION, {
      x: this.#player.sprite.x,
      y: this.#player.sprite.y,
    });

    // broadcast the new position to other players in the realm
    netManager.client?.sendMove(
      { x: this.#player.sprite.x, y: this.#player.sprite.y },
      toNetDir(this.#player.direction),
      false
    );

    // update camera bounds for given level after player moves
    if (CameraUtils.updateMainCameraBounds(this, this.#player.sprite, this.#cameraRegions)) {
      // Bounds shrank/grew to a new camera region → refit the zoom so the viewport stays filled.
      this.#fitCamera();
    }

    // check to see if the player encountered cut scene zone
    this.#player.sprite.getBounds(this.#rectangleForOverlapCheck1);
    for (const zone of Object.values(this.#eventZones)) {
      // get the bounds of the player and zone for checking for overlap
      zone.getBounds(this.#rectangleForOverlapCheck2);

      // reset rectangle overlap size, needed since method below will
      // return the original rectangle unmodified if a previous match
      // was found.
      this.#rectangleOverlapResult.setSize(0, 0);
      Phaser.Geom.Intersects.GetRectangleIntersection(
        this.#rectangleForOverlapCheck1,
        this.#rectangleForOverlapCheck2,
        this.#rectangleOverlapResult
      );

      if (ENABLE_ZONE_DEBUGGING) {
        // for debugging the overlap checks for the events
        this.#gfx.clear();
        this.#gfx.strokeRectShape(this.#rectangleOverlapResult);
      }

      const isOverlapping =
        this.#rectangleOverlapResult.width >= TILE_SIZE - 10 && this.#rectangleOverlapResult.height >= TILE_SIZE - 10;

      if (isOverlapping) {
        const eventId = parseInt(zone.name, 10);
        const eventData = DataUtils.getEventData(this, eventId);
        const currentGameFlags = dataManager.getFlags();
        const eventRequirementsMet = eventData.requires.every((flag) => {
          return currentGameFlags.has(flag);
        });
        if (eventRequirementsMet) {
          this.#currentCutSceneId = parseInt(zone.name, 10);
          this.#startCutScene();
          break;
        }
      }
    }
    if (this.#currentCutSceneId !== undefined) {
      return;
    }

    // visible-mob encounters are the PRIMARY source: bumping a mob starts its battle
    this.#checkMobCollision();
    if (this.#wildMonsterEncountered) {
      return;
    }

    if (this.#encounterLayers.length === 0) {
      return;
    }
    this.#handlePlayerMovementInEncounterZone();
  }

  /**
   * @returns {void}
   */
  #handlePlayerMovementInEncounterZone() {
    // cleanup any special tiles that are not at the players current position
    this.#specialEncounterTileImageGameObjectGroup
      .getChildren()
      .forEach((/** @type {Phaser.GameObjects.Image} */ child) => {
        if (!child.active) {
          return;
        }
        if (child.x === this.#player.sprite.x && child.y === this.#player.sprite.y) {
          child.visible = true;
          return;
        }
        child.active = false;
        child.visible = false;
      });
    if (this.#encounterZonePlayerIsEntering === undefined) {
      return;
    }

    // low-rate fallback: visible roaming mobs are the primary encounter source now, so the
    // invisible-grass trigger is reduced from 20% → 5% to stay as a light surprise only.
    this.#wildMonsterEncountered = Math.random() < (isEventActive() ? EVENT_ENCOUNTER_RATE : 0.05);
    if (this.#wildMonsterEncountered) {
      // CRITICAL: anything that throws/no-ops here used to leave the player FROZEN (movement is locked
      // by #wildMonsterEncountered) with no battle ever appearing. So validate the encounter monster
      // and UNLOCK on any failure — the player must never get stuck.
      try {
        const encounterAreaId = /** @type {import('../types/typedef.js').TiledObjectProperty[]} */ (
          this.#encounterZonePlayerIsEntering.layer.properties
        ).find((property) => property.name === TILED_ENCOUNTER_PROPERTY.AREA)?.value;
        const possibleMonsters = DataUtils.getEncounterAreaDetails(this, encounterAreaId);
        const localMonsterId =
          Array.isArray(possibleMonsters) && possibleMonsters.length ? weightedRandom(possibleMonsters) : undefined;
        if (localMonsterId === undefined || localMonsterId === null) {
          // no valid encounter monster for this zone → don't lock the player into a battle that can't start
          this.#wildMonsterEncountered = false;
          return;
        }
        // server-authoritative enemy + reward when available; falls back to the local enemy. On ANY
        // failure to start, unlock movement so the player is never stranded (no more "frozen, no battle").
        this.#startWildBattle(encounterAreaId, localMonsterId).catch((err) => {
          console.error('[battle] wild battle failed to start — unlocking movement', err);
          this.#wildMonsterEncountered = false;
        });
      } catch (err) {
        console.error('[battle] encounter setup failed — unlocking movement', err);
        this.#wildMonsterEncountered = false;
      }
    }
  }

  /**
   * Build the wild-encounter battle data and start the battle. Tries a single-use server battle
   * session first (server-chosen enemy + reward-bearing sessionId); on ANY failure — offline,
   * api down, timeout, walletless — it degrades to the LOCAL enemy exactly as the game does today
   * and passes no session id (the battle scene then uses the legacy /player/battle-win reward).
   * This NEVER throws and never blocks the encounter: the enemy lock is set by the caller.
   * @param {string|number} encounterAreaId tiled/encounters area id
   * @param {number} localMonsterId the locally-rolled fallback monster id (used when offline)
   * @returns {Promise<void>}
   */
  async #startWildBattle(encounterAreaId, localMonsterId) {
    /** @type {string | undefined} */
    let battleSessionId;
    /** @type {import('../types/typedef.js').Monster | undefined} */
    let enemyMonster;

    try {
      const session = await startBattleSession(netManager.wallet, encounterAreaId);
      if (session) {
        const serverEnemy = DataUtils.getMonsterById(this, session.monsterId);
        if (serverEnemy) {
          // apply the server-chosen level (drives the server-authoritative reward + exp) AND scale
          // the enemy's HP to that level (templateHp as the lv-1 base) so a higher-level enemy is
          // actually tankier — levels were previously cosmetic for stats. Attack scales in the
          // damage calc via attackAtLevel().
          serverEnemy.maxHp = hpAtLevel(serverEnemy.maxHp, session.level);
          serverEnemy.currentHp = serverEnemy.maxHp;
          serverEnemy.currentLevel = session.level;
          // Match exp to the level too, else a captured high-level beast keeps the template's tiny
          // currentExp (lv-16 base) and needs an absurd grind to gain its first level.
          serverEnemy.currentExp = totalExpNeededForLevel(session.level);
          enemyMonster = serverEnemy;
          battleSessionId = session.sessionId;
        }
      }
    } catch (err) {
      // defensive: startBattleSession already swallows errors, but never let the encounter break.
      console.warn('[battle] session start failed — using local enemy', err);
    }

    // fallback (and the default path when no session): local enemy, scaled to its own level.
    if (!enemyMonster) {
      enemyMonster = DataUtils.getMonsterById(this, localMonsterId);
      if (enemyMonster) {
        enemyMonster.maxHp = hpAtLevel(enemyMonster.maxHp, enemyMonster.currentLevel);
        enemyMonster.currentHp = enemyMonster.maxHp;
        enemyMonster.currentExp = totalExpNeededForLevel(enemyMonster.currentLevel);
      }
    }
    // If we STILL couldn't resolve an enemy (bad/missing monster id), passing [undefined] to the
    // battle scene crashes its create() → the player is soft-locked (movement stays locked, no battle).
    // Bail and unlock instead.
    if (!enemyMonster) {
      console.error('[battle] no enemy monster resolved — unlocking movement', localMonsterId);
      this.#wildMonsterEncountered = false;
      return;
    }

    /** @type {import('./battle-scene.js').BattleSceneData} */
    const dataToPass = {
      enemyMonsters: [enemyMonster],
      playerMonsters: dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY),
      // realm/area let the battle scene pick its backdrop (Cave → cavern, else grassland arena).
      realm: this.#sceneData.realm,
      area: this.#sceneData.area,
    };
    if (battleSessionId) {
      dataToPass.battleSessionId = battleSessionId;
    }
    this.#startBattleScene(dataToPass);
  }

  /**
   * @returns {boolean}
   */
  /** @type {number} current map pixel width — drives the cover-fit camera zoom on resize. */
  #worldMapPixelW = 0;
  /** @type {number} current map pixel height. */
  #worldMapPixelH = 0;

  /**
   * Cover-fit the main camera: use the base 0.8 zoom for the big overworld, but zoom UP when a map
   * is narrower/shorter than the viewport (e.g. the 40-wide Town) so the world ALWAYS fills the
   * screen and never leaves a black void at an edge. Re-run on every RESIZE.
   * @returns {void}
   */
  /** A grass/cave-themed camera clear color so an out-of-map area never reads as a pure-black void. */
  #backdropColor() {
    const area = this.#sceneData?.area ?? '';
    if (area.startsWith('cave')) return 0x161018; // dark cavern
    if (area.startsWith('forest')) return 0x223a1c; // deep forest
    return 0x4e7d39; // grass — town / overworld / default
  }

  #fitCamera() {
    const baseZoom = 0.8;
    const w = this.scale.width;
    const h = this.scale.height;
    // Cover-fit to the CURRENT camera bounds, NOT the raw map size. A camera region can shrink the
    // bounds below the full map (e.g. the Town's 1536-wide region inside a 2560-wide map); fitting the
    // raw map then under-zoomed and left a void at the screen edge. Filling the actual bounds removes it.
    const b = this.cameras.main.getBounds();
    const mw = b.width || this.#worldMapPixelW || w;
    const mh = b.height || this.#worldMapPixelH || h;
    // The cover-fit value is the floor (below it a void/backdrop shows at the edges); the player's
    // #userZoom (mouse wheel / pinch) multiplies it so they can zoom in to examine or out to see more.
    this.cameras.main.setZoom(Math.max(baseZoom, w / mw, h / mh) * this.#userZoom);
  }

  /** Player-controlled zoom multiplier on top of the cover-fit (1 = default, >1 in, <1 out). */
  #userZoom = 1;
  #lastPinchDist = 0;

  /**
   * Wire mouse-wheel + two-finger pinch to zoom the world camera, clamped so it never under-zooms
   * into a big void. Called once from create().
   * @returns {void}
   */
  #setupZoomControls() {
    const apply = (factor) => {
      this.#userZoom = Phaser.Math.Clamp(this.#userZoom * factor, 0.85, 2.2);
      this.#fitCamera();
    };
    this.input.on(Phaser.Input.Events.POINTER_WHEEL, (_p, _go, _dx, dy) => {
      apply(dy > 0 ? 0.92 : 1.08); // wheel up = zoom in
    });
    this.input.on(Phaser.Input.Events.POINTER_MOVE, () => {
      const p1 = this.input.pointer1;
      const p2 = this.input.pointer2;
      if (p1?.isDown && p2?.isDown) {
        const dist = Phaser.Math.Distance.Between(p1.x, p1.y, p2.x, p2.y);
        if (this.#lastPinchDist > 0 && dist > 0) apply(dist / this.#lastPinchDist);
        this.#lastPinchDist = dist;
      } else {
        this.#lastPinchDist = 0;
      }
    });
  }

  /**
   * Self-heal stale party stats on load. Recomputes each monster's maxHp (from its species template +
   * level) and clamps currentExp into the valid band for its level — fixing beasts that were caught or
   * restored before stat-scaling existed (tiny HP) and high-level captures stuck with the template's
   * lv-16 exp (which made the next level-up take ~10x too long). Idempotent. A monster that was at full
   * HP is re-filled to its new max; a damaged one keeps its (clamped) HP — so this never free-heals.
   * @returns {void}
   */
  #normalizePartyStats() {
    let party = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY);
    if (!Array.isArray(party) || party.length === 0) return;
    let changed = false;
    // STRIP null/invalid slots. A null in the party crashed the battle UI ("Cannot read currentHp of
    // null"); removing them here cleans the persisted party so it can't recur. A real monster always
    // has a numeric monsterId.
    const cleaned = party.filter((m) => m && typeof m.monsterId === 'number');
    if (cleaned.length !== party.length) {
      party = cleaned;
      changed = true;
    }
    party.forEach((m) => {
      if (!m || typeof m.currentLevel !== 'number') return;
      const template = DataUtils.getMonsterById(this, m.monsterId);
      if (!template) return;
      // Re-sync the SPRITE + type from the species template by monsterId. The saved party bakes these
      // in at catch/evolve time, so a sprite remap (or an evolution done under old data) leaves stale
      // art — e.g. an evolved Pyrothar still showing the old Grazeling sprite. The monsterId is the
      // source of truth for the species, so pull the current sprite/scale/element from the template.
      if (m.assetKey !== template.assetKey) {
        m.assetKey = template.assetKey;
        changed = true;
      }
      if (m.assetScale !== (template.assetScale || 1)) {
        m.assetScale = template.assetScale || 1;
        changed = true;
      }
      if (m.assetFrame !== (template.assetFrame || 0)) {
        m.assetFrame = template.assetFrame || 0;
        changed = true;
      }
      if (template.element && m.element !== template.element) {
        m.element = template.element;
        changed = true;
      }
      // Re-sync the MOVESET from the species template too. Moves are species-defined (not individual),
      // so a monster that evolved before the moveset fix (e.g. a Solanoid still holding baby Solani's
      // Tackle/Poison Sting) self-heals to its real moves here.
      if (Array.isArray(template.attackIds) && template.attackIds.length > 0) {
        const cur = Array.isArray(m.attackIds) ? m.attackIds : [];
        if (cur.length !== template.attackIds.length || cur.some((id, i) => id !== template.attackIds[i])) {
          m.attackIds = [...template.attackIds];
          changed = true;
        }
      }
      const wasFull = typeof m.currentHp === 'number' && m.currentHp >= m.maxHp;
      const scaledMax = hpAtLevel(template.maxHp, m.currentLevel);
      if (m.maxHp !== scaledMax) {
        m.maxHp = scaledMax;
        changed = true;
      }
      const hp = wasFull ? m.maxHp : Math.min(typeof m.currentHp === 'number' ? m.currentHp : m.maxHp, m.maxHp);
      if (m.currentHp !== hp) {
        m.currentHp = hp;
        changed = true;
      }
      const lo = totalExpNeededForLevel(m.currentLevel);
      const hi = totalExpNeededForLevel(m.currentLevel + 1);
      if (typeof m.currentExp !== 'number' || m.currentExp < lo || m.currentExp >= hi) {
        m.currentExp = lo;
        changed = true;
      }
    });
    if (changed) dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, party);
  }

  /**
   * Show the virtual joystick on touch devices OR phone-sized viewports; hide otherwise.
   *
   * @param {boolean} [force=false] When true (called from create()), tear down any existing /
   *   mid-shutdown controls instance and re-launch a fresh one. World→world transitions (portals /
   *   building interiors) restart this scene; the previous world's SHUTDOWN stops the mobile
   *   controls, but that stop can still be in flight when the new create() runs — so a plain
   *   `isActive()` check would see the stale instance and skip the re-launch, leaving the joystick
   *   gone and the player stuck inside buildings. Forcing stop()→launch() (as the HUD scenes do)
   *   safely supersedes the pending shutdown. On RESIZE we pass force=false so an in-progress
   *   joystick drag isn't interrupted by a needless relaunch.
   * @returns {void}
   */
  /**
   * RESIZE handler. Phaser passes the new gameSize as the first arg, so we can't bind
   * #updateMobileControls directly (its first param is `force`). Always re-evaluate without forcing.
   * @returns {void}
   */
  #onMobileControlsResize() {
    this.#updateMobileControls(false);
  }

  #updateMobileControls(force = false) {
    const w = typeof window !== 'undefined' ? window : undefined;
    const coarse =
      !!w &&
      (!!(w.matchMedia && w.matchMedia('(pointer: coarse)').matches) ||
        'ontouchstart' in w ||
        (!!w.navigator && (w.navigator.maxTouchPoints || 0) > 0));
    const narrow = Math.min(this.scale.width, this.scale.height) <= 820;
    const want = coarse || narrow;
    const active = this.scene.isActive(SCENE_KEYS.MOBILE_CONTROLS_SCENE);
    if (want) {
      if (force && active) {
        this.scene.stop(SCENE_KEYS.MOBILE_CONTROLS_SCENE);
      }
      if (force || !active) {
        this.scene.launch(SCENE_KEYS.MOBILE_CONTROLS_SCENE);
        this.scene.bringToTop(SCENE_KEYS.MOBILE_CONTROLS_SCENE);
      }
    } else if (active) {
      this.scene.stop(SCENE_KEYS.MOBILE_CONTROLS_SCENE);
    }
  }

  #isPlayerInputLocked() {
    return (
      this._controls.isInputLocked ||
      this.#dialogUi.isVisible ||
      this.#menu.isVisible ||
      this.#isProcessingNpcEvent ||
      this.#currentCutSceneId !== undefined ||
      // the evolution celebration runs as an overlay over the world — freeze movement/encounters
      // while it's up so the player can't walk into a wild battle mid-celebration (soft-lock).
      this.scene.isActive(SCENE_KEYS.EVOLUTION_SCENE)
    );
  }

  /**
   * Fast-travel to another area's default spawn — invoked by the Map UI so players can reach the
   * Town / Wilds without trekking to an edge warp. Reads the TARGET map's player-spawn, sets the
   * position, fades, and restarts the world scene there. No-ops for an unknown area (no map).
   * @param {string} area target area key (e.g. 'main_1', 'overworld_1')
   * @returns {void}
   */
  travelToArea(area) {
    if (!area || area === this.#sceneData.area) return;
    try {
      const map = this.#getLevelTileMap(`${area.toUpperCase()}_LEVEL`);
      const spawn = map?.getObjectLayer(OBJECT_LAYER_NAMES.PLAYER_SPAWN_LOCATION)?.objects?.[0];
      if (!map || !spawn) return; // area has no loaded map (e.g. badlands) → caller already guards
      dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_POSITION, { x: spawn.x, y: spawn.y - TILE_SIZE });
      this._controls.lockInput = true;
      // Fixed-timer + event (whichever fires first) so a missed FADE_OUT_COMPLETE can't strand the
      // player on a black screen — same robustness as #startBattleScene.
      this.cameras.main.fadeOut(400, 0, 0, 0);
      let traveled = false;
      const goTravel = () => {
        if (traveled) return;
        traveled = true;
        this.scene.start(SCENE_KEYS.WORLD_SCENE, { area, isInterior: false });
      };
      this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, goTravel);
      this.time.delayedCall(450, goTravel);
    } catch (err) {
      console.warn('[WorldScene:travelToArea] failed', err);
      this._controls.lockInput = false;
    }
  }

  /**
   * @param {Phaser.Tilemaps.Tilemap} map
   * @returns {void}
   */
  #createNPCs(map) {
    this.#npcs = [];

    const npcLayers = map.getObjectLayerNames().filter((layerName) => layerName.includes('NPC'));
    npcLayers.forEach((layerName) => {
      const layer = map.getObjectLayer(layerName);
      const npcObject = layer.objects.find((obj) => {
        return obj.type === CUSTOM_TILED_TYPES.NPC;
      });
      if (!npcObject || npcObject.x === undefined || npcObject.y === undefined) {
        return;
      }

      // get the path objects for this npc
      const pathObjects = layer.objects.filter((obj) => {
        return obj.type === CUSTOM_TILED_TYPES.NPC_PATH;
      });
      /** @type {import('../world/characters/npc.js').NPCPath} */
      const npcPath = {
        0: { x: npcObject.x, y: npcObject.y - TILE_SIZE },
      };
      pathObjects.forEach((obj) => {
        if (obj.x === undefined || obj.y === undefined) {
          return;
        }
        npcPath[parseInt(obj.name, 10)] = { x: obj.x, y: obj.y - TILE_SIZE };
      });

      /** @type {import('../world/characters/npc.js').NpcMovementPattern} */
      const npcMovement =
        /** @type {import('../types/typedef.js').TiledObjectProperty[]} */ (npcObject.properties).find(
          (property) => property.name === TILED_NPC_PROPERTY.MOVEMENT_PATTERN
        )?.value || 'IDLE';

      /** @type {number} */
      const npcId = /** @type {import('../types/typedef.js').TiledObjectProperty[]} */ (npcObject.properties).find(
        (property) => property.name === TILED_NPC_PROPERTY.ID
      )?.value;
      const npcDetails = DataUtils.getNpcData(this, npcId);
      // Guard: an NPC object with a missing/unknown id → getNpcData returns undefined; dereferencing
      // .frame below would throw and white-screen the whole map. Skip it instead.
      if (!npcDetails) {
        console.warn(`[WorldScene:#createNPCs] no npc data for id ${npcId} — skipping`);
        return;
      }

      // In Tiled, the x value is how far the object starts from the left, and the y is the bottom of tiled object that is being added
      const npc = new NPC({
        scene: this,
        position: { x: npcObject.x, y: npcObject.y - TILE_SIZE },
        direction: DIRECTION.DOWN,
        frame: npcDetails.frame,
        npcPath,
        movementPattern: npcMovement,
        events: npcDetails.events,
        animationKeyPrefix: npcDetails.animationKeyPrefix,
        id: npcId,
      });
      this.#npcs.push(npc);
    });
  }

  /**
   * @returns {void}
   */
  #handlePlayerDirectionUpdate() {
    // update player direction on global data store
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_DIRECTION, this.#player.direction);

    // broadcast the facing change to other players in the realm
    netManager.client?.sendMove(
      { x: this.#player.sprite.x, y: this.#player.sprite.y },
      toNetDir(this.#player.direction),
      true
    );
  }

  /**
   * @returns {void}
   */
  #healPlayerParty() {
    // heal all monsters in party
    /** @type {import('../types/typedef.js').Monster[]} */
    const monsters = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY);
    monsters.forEach((monster) => {
      monster.currentHp = monster.maxHp;
    });
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, monsters);
    // persist the restored HP immediately so an on-demand heal (e.g. the Monstara Center nurse)
    // survives a reload without requiring a manual Save.
    dataManager.saveData();
  }

  /**
   * Evolve any party monster that has crossed its evolution level and, if at least one evolved,
   * launch the celebration overlay over the world. Runs OUTSIDE battle (on world entry / battle
   * return). Wrapped in a try/catch so a celebration can never wedge world movement.
   * @returns {void}
   */
  #runEvolutionCheck() {
    try {
      const evolutions = checkAndEvolveParty(this);
      if (evolutions.length > 0) {
        this.scene.launch(SCENE_KEYS.EVOLUTION_SCENE, { evolutions });
        this.scene.bringToTop(SCENE_KEYS.EVOLUTION_SCENE);
      }
    } catch (error) {
      console.warn(`[${WorldScene.name}:runEvolutionCheck] evolution check failed`, error);
    }
  }

  /**
   * @param {Phaser.Tilemaps.Tilemap} map
   * @returns {void}
   */
  #createItems(map) {
    const itemObjectLayer = map.getObjectLayer(OBJECT_LAYER_NAMES.ITEM);
    if (!itemObjectLayer) {
      return;
    }
    const items = itemObjectLayer.objects;
    const validItems = items.filter((item) => {
      return item.x !== undefined && item.y !== undefined;
    });

    /** @type {number[]} */
    const itemsPickedUp = dataManager.store.get(DATA_MANAGER_STORE_KEYS.ITEMS_PICKED_UP) || [];

    for (const tiledItem of validItems) {
      /** @type {number} */
      const itemId = /** @type {import('../types/typedef.js').TiledObjectProperty[]} */ (tiledItem.properties).find(
        (property) => property.name === TILED_ITEM_PROPERTY.ITEM_ID
      )?.value;

      /** @type {number} */
      const id = /** @type {import('../types/typedef.js').TiledObjectProperty[]} */ (tiledItem.properties).find(
        (property) => property.name === TILED_ITEM_PROPERTY.ID
      )?.value;

      if (itemsPickedUp.includes(id)) {
        continue;
      }

      // create object
      const item = new Item({
        scene: this,
        position: {
          x: tiledItem.x,
          y: tiledItem.y - TILE_SIZE,
        },
        itemId,
        id,
      });
      this.#items.push(item);
    }
  }

  /**
   * Render decorative low-poly props (torches, barrels, chests, flags, rocks, …) from the
   * `Decor` object layer. Non-blocking visuals; created before the player so the player
   * walks in front of them.
   * @param {Phaser.Tilemaps.Tilemap} map
   * @returns {void}
   */
  #createDecor(map) {
    const layer = map.getObjectLayer('Decor');
    if (!layer) {
      return;
    }
    layer.objects.forEach((obj) => {
      const frame = obj.properties?.find((p) => p.name === 'frame')?.value;
      const scale = obj.properties?.find((p) => p.name === 'scale')?.value ?? 0.5;
      if (!frame) {
        return;
      }
      this.add
        .sprite(obj.x + TILE_SIZE / 2, obj.y - TILE_SIZE / 2, LOWPOLY_ASSET_KEYS.PROPS, frame)
        .setOrigin(0.5, 0.7)
        .setScale(scale)
        .setDepth(0);
    });
  }

  /**
   * Find the world position of a building's door on its (exterior) source map. Used by the
   * faint-into-a-building round-trip so exiting the interior drops the player back at the door.
   * Mirrors the entry round-trip in #handleEntranceEnteredCallback: returnPosition is the door
   * tile and exit offsets one tile below it so the player doesn't immediately re-enter.
   * @param {string} sourceArea the exterior map the building is reached from (e.g. 'main_1')
   * @param {string} buildingArea the building interior area key (e.g. 'building_1')
   * @returns {import('../types/typedef.js').Coordinate | undefined}
   */
  #getBuildingEntrancePosition(sourceArea, buildingArea) {
    try {
      const map = this.#getLevelTileMap(`${sourceArea.toUpperCase()}_LEVEL`);
      const entranceLayer = map.getObjectLayer(OBJECT_LAYER_NAMES.SCENE_TRANSITIONS);
      const entrance = entranceLayer?.objects.find((object) => {
        const connectsTo = /** @type {import('../types/typedef.js').TiledObjectProperty[]} */ (
          object.properties
        )?.find((property) => property.name === 'connects_to')?.value;
        return connectsTo === buildingArea;
      });
      if (!entrance) return undefined;
      return { x: entrance.x, y: entrance.y };
    } catch (_e) {
      return undefined;
    }
  }

  /**
   * @param {string} entranceName
   * @param {string} entranceId
   * @param {boolean} isBuildingEntrance
   * @returns {void}
   */
  #handleEntranceEnteredCallback(entranceName, entranceId, isBuildingEntrance) {
    // Level-gate the Cave — a higher-tier zone (Badlands-tier creatures, ~2× the Gold). Block entry
    // until the player reaches CAVE_UNLOCK_LEVEL so newcomers aren't thrown into lv 8-20 fights.
    if (typeof entranceName === 'string' && entranceName.toLowerCase() === 'cave_1') {
      const xp = dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_XP) || 0;
      if (playerLevel(xp) < CAVE_UNLOCK_LEVEL) {
        this.#showGateMessage(`Cave sealed — reach Lv. ${CAVE_UNLOCK_LEVEL} to enter`);
        return;
      }
    }
    this._controls.lockInput = true;

    // ── Interiors use a position-based round-trip ──
    // A building can be entered from ANY map (the overworld village OR the town) and its door
    // always returns you to the exact tile you walked in from. We remember (returnArea,
    // returnPosition) on entry and restore it on exit, so interiors need no per-source spawn data.
    const insideInterior = this.#sceneData?.isInterior === true;

    /** @type {number} */ let x;
    /** @type {number} */ let y;
    /** @type {import('@mn/shared').RealmId} */ let toRealm;
    /** @type {WorldSceneData} */ let dataToPass;

    if (insideInterior) {
      // Walking into the interior's door → go back outside to where we came in.
      const back = this.#sceneData.returnArea ?? 'main_1';
      const pos = this.#sceneData.returnPosition;
      toRealm = realmForArea(back);
      x = pos ? pos.x : this.#player.sprite.x;
      y = pos ? pos.y + TILE_SIZE : this.#player.sprite.y; // a tile below the door, so we don't re-enter
      dataToPass = { area: back, isInterior: false, realm: toRealm };
    } else {
      const map = this.#getLevelTileMap(`${entranceName.toUpperCase()}_LEVEL`);
      const entranceObjectLayer = map.getObjectLayer(OBJECT_LAYER_NAMES.SCENE_TRANSITIONS);
      let entranceObject;
      if (isBuildingEntrance) {
        // Spawn just inside the building's own (single) door, regardless of source map.
        entranceObject = entranceObjectLayer.objects[0];
        x = entranceObject.x;
        y = entranceObject.y - TILE_SIZE * 2; // clearly inside the room, off the door trigger
      } else {
        entranceObject = entranceObjectLayer.objects.find((object) => {
          const tempEntranceName = object.properties.find((property) => property.name === 'connects_to').value;
          const tempEntranceId = object.properties.find((property) => property.name === 'entrance_id').value;
          return tempEntranceName === this.#sceneData.area && tempEntranceId === entranceId;
        });
        x = entranceObject.x;
        y = entranceObject.y - TILE_SIZE;
        if (this.#player.direction === DIRECTION.UP) y -= TILE_SIZE;
        if (this.#player.direction === DIRECTION.DOWN) y += TILE_SIZE;
      }

      /** @type {import('@mn/shared').RealmId | undefined} */
      const realmOverride = /** @type {import('../types/typedef.js').TiledObjectProperty[]} */ (
        entranceObject.properties
      ).find((property) => property.name === 'realm')?.value;
      toRealm = realmOverride ?? realmForArea(entranceName);
      dataToPass = { area: entranceName, isInterior: isBuildingEntrance, realm: toRealm };
      if (isBuildingEntrance) {
        dataToPass.returnArea = this.#sceneData.area;
        dataToPass.returnPosition = { x: this.#player.sprite.x, y: this.#player.sprite.y };
      }
    }

    // If we cross a realm boundary, tell the server before transitioning.
    if (toRealm !== this.#netRealm) {
      netManager.client?.enterPortal(toRealm);
    }

    // Fixed-timer + fade-complete (whichever fires first) so a missed completion can't strand the
    // player on a black screen during a portal/building transition (same fix as #startBattleScene).
    // Shortened 1000ms → 450ms to match the snappier battle/travel fades.
    this.cameras.main.fadeOut(450, 0, 0, 0);
    let warped = false;
    const goWarp = () => {
      if (warped) return;
      warped = true;
      dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_POSITION, { x, y });
      this.scene.start(SCENE_KEYS.WORLD_SCENE, dataToPass);
    };
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, goWarp);
    this.time.delayedCall(500, goWarp);
  }

  /**
   * Drop a single collectable gold coin on a random walkable tile a few tiles from the player.
   * Capped at 3 coins on the map. Coins are plain graphics (no asset needed) with an idle bob.
   * @returns {void}
   */
  #spawnGoldCoin() {
    if (!this.#goldCoins || this.#goldCoins.length >= 3 || !this.#player) {
      return;
    }
    const px = Math.round(this.#player.sprite.x / TILE_SIZE);
    const py = Math.round(this.#player.sprite.y / TILE_SIZE);
    // BFS out from the player so the coin only lands on a tile they can actually WALK to. A plain
    // collision check could pick a walkable-but-isolated tile (behind a wall / across water / a fenced
    // pocket) the player can never reach — the reported "gold I can't get to".
    const walkable = (tx, ty) => {
      const t = this.#collisionLayer?.getTileAtWorldXY(tx * TILE_SIZE + TILE_SIZE / 2, ty * TILE_SIZE + TILE_SIZE / 2, true);
      return !t || t.index === -1;
    };
    const RADIUS = 10;
    const seen = new Set([`${px},${py}`]);
    const queue = [[px, py]];
    const reachable = [];
    while (queue.length) {
      const [x, y] = queue.shift();
      const dist = Math.abs(x - px) + Math.abs(y - py);
      if (dist >= 3 && dist <= RADIUS) reachable.push([x, y]); // far enough to be worth walking to
      if (dist >= RADIUS) continue;
      for (const [nx, ny] of [[x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]]) {
        const k = `${nx},${ny}`;
        if (seen.has(k)) continue;
        seen.add(k);
        if (walkable(nx, ny)) queue.push([nx, ny]);
      }
    }
    if (reachable.length === 0) return;
    const [tx, ty] = reachable[Phaser.Math.Between(0, reachable.length - 1)];
    const wx = tx * TILE_SIZE + TILE_SIZE / 2;
    const wy = ty * TILE_SIZE + TILE_SIZE / 2;
    const value = Phaser.Math.Between(5, 15);
    const coin = this.add.circle(0, 0, 11, 0xf5b23e).setStrokeStyle(3, 0xffe899);
    const inner = this.add.circle(0, 0, 5, 0xfff0b8);
    const grp = this.add.container(wx, wy, [coin, inner]).setDepth(3);
    this.tweens.add({
      targets: grp,
      y: wy - 6,
      scaleX: 1.12,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.#goldCoins.push({ grp, x: wx, y: wy, value });
  }

  /**
   * Collect any gold coin the player is standing on: credit Gold locally (instant HUD update) and
   * persist server-side via /player/collect-gold (server-capped). Cheap distance check each frame.
   * @returns {void}
   */
  #collectNearbyGold() {
    if (!this.#goldCoins?.length || !this.#player) {
      return;
    }
    const cx = this.#player.sprite.x + TILE_SIZE / 2;
    const cy = this.#player.sprite.y + TILE_SIZE / 2;
    const r2 = (TILE_SIZE * 0.8) ** 2;
    for (let i = this.#goldCoins.length - 1; i >= 0; i--) {
      const c = this.#goldCoins[i];
      const dx = cx - c.x;
      const dy = cy - c.y;
      if (dx * dx + dy * dy > r2) continue;
      this.#goldCoins.splice(i, 1);
      c.grp.destroy();
      playSoundFx(this, AUDIO_ASSET_KEYS.COIN, 0.18); // single retro coin "ding" on pickup — kept soft
      dataManager.setGold(dataManager.getGold() + c.value); // instant HUD credit
      void collectGold(netManager.wallet, c.value); // persist to DB (server-capped)
      this.#showGoldFlash(c.x, c.y, c.value);
    }
  }

  /**
   * Floating "+N" gold text that rises and fades at a world position.
   * @param {number} x
   * @param {number} y
   * @param {number} value
   * @returns {void}
   */
  /**
   * Flash a short notice above the player (e.g. a locked-zone message). World-positioned like the
   * gold flash, so it reads wherever the player is standing; holds briefly then floats up + fades.
   * @param {string} text
   * @returns {void}
   */
  #showGateMessage(text) {
    this.cameras.main.shake(120, 0.004);
    const px = this.#player.sprite.x;
    const py = this.#player.sprite.y;
    const t = this.add
      .text(px, py - 28, text, {
        fontFamily: 'Kenney-Future-Narrow, system-ui, sans-serif',
        fontSize: '16px',
        color: '#ffe7a3',
        stroke: '#06140d',
        strokeThickness: 4,
        align: 'center',
      })
      .setOrigin(0.5, 1)
      .setDepth(4000);
    this.tweens.add({
      targets: t,
      y: py - 64,
      alpha: 0,
      delay: 1100,
      duration: 700,
      ease: 'Cubic.easeOut',
      onComplete: () => t.destroy(),
    });
  }

  /**
   * @param {number} x
   * @param {number} y
   * @param {number} value
   * @returns {void}
   */
  #showGoldFlash(x, y, value) {
    // a tiny camera shake adds satisfying "juice" to picking up Gold (was silent + flat)
    this.cameras.main.shake(90, 0.0035);
    const t = this.add
      .text(x, y - 10, `+${value}`, {
        fontFamily: 'Kenney-Future-Narrow, monospace',
        fontSize: '18px',
        color: '#ffd34d',
        stroke: '#06140d',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(3000);
    this.tweens.add({
      targets: t,
      y: y - 52,
      alpha: 0,
      duration: 900,
      ease: 'Cubic.easeOut',
      onComplete: () => t.destroy(),
    });
  }

  /**
   * @returns {void}
   */
  #handleNpcInteraction() {
    if (this.#isProcessingNpcEvent) {
      return;
    }

    // check to see if the npc has any events associated with them
    const isMoreEventsToProcess = this.#npcPlayerIsInteractingWith.events.length - 1 !== this.#lastNpcEventHandledIndex;

    if (!isMoreEventsToProcess) {
      this.#npcPlayerIsInteractingWith.isTalkingToPlayer = false;
      this.#npcPlayerIsInteractingWith = undefined;
      this.#lastNpcEventHandledIndex = -1;
      this.#isProcessingNpcEvent = false;
      return;
    }

    // get the next event from the queue and process for this npc
    this.#lastNpcEventHandledIndex += 1;
    const eventToHandle = this.#npcPlayerIsInteractingWith.events[this.#lastNpcEventHandledIndex];
    const eventType = eventToHandle.type;

    // check to see if this event should be handled based on story flags
    const currentGameFlags = dataManager.getFlags();
    const eventRequirementsMet = eventToHandle.requires.every((flag) => {
      if (flag === BATTLE_FLAG.TRAINER_DEFEATED) {
        return dataManager.getDefeatedNpcs().has(this.#npcPlayerIsInteractingWith.id);
      }
      if (flag === BATTLE_FLAG.TRAINER_NOT_DEFEATED) {
        return !dataManager.getDefeatedNpcs().has(this.#npcPlayerIsInteractingWith.id);
      }

      return currentGameFlags.has(flag);
    });
    if (!eventRequirementsMet) {
      // jump to next event
      this.#handleNpcInteraction();
      return;
    }

    switch (eventType) {
      case NPC_EVENT_TYPE.MESSAGE:
        this.#dialogUi.showDialogModal(eventToHandle.data.messages);
        break;
      case NPC_EVENT_TYPE.OPEN_CARD_SHOP:
        // Shopkeeper routes the player into the core loop: close out the npc conversation,
        // then open the Card Shop over the (now paused) world.
        this.#npcPlayerIsInteractingWith.isTalkingToPlayer = false;
        this.#npcPlayerIsInteractingWith = undefined;
        this.#lastNpcEventHandledIndex = -1;
        this.#isProcessingNpcEvent = false;
        this.openCardShop();
        break;
      case NPC_EVENT_TYPE.OPEN_PC_BOX:
        // Storage Keeper routes the player into the PC Box (beast storage) — a town house's purpose.
        this.#npcPlayerIsInteractingWith.isTalkingToPlayer = false;
        this.#npcPlayerIsInteractingWith = undefined;
        this.#lastNpcEventHandledIndex = -1;
        this.#isProcessingNpcEvent = false;
        this.openPcBox();
        break;
      case NPC_EVENT_TYPE.HEAL: {
        this.#isProcessingNpcEvent = true;
        // The healing center charges a small Gold fee (a sink). Knockout recovery stays FREE — that's
        // handled separately via #healPlayerParty() on world entry after a defeat.
        const HEAL_FEE = 100;
        if ((dataManager.getGold() || 0) < HEAL_FEE) {
          this.#dialogUi.showDialogModal([`The healer needs ${HEAL_FEE} Gold to treat your team — win a few battles first!`]);
        } else {
          // optimistic: deduct + heal locally, persist the fee server-side (authoritative balance on return)
          dataManager.setGold(dataManager.getGold() - HEAL_FEE);
          this.#healPlayerParty();
          void healParty(netManager.wallet).then((res) => {
            if (res && res.ok && typeof res.gold === 'number') dataManager.setGold(res.gold);
          });
          this.#dialogUi.showDialogModal([`Your team is fully healed! (-${HEAL_FEE} Gold)`]);
        }
        this.#isProcessingNpcEvent = false;
        this.#handleNpcInteraction();
        break;
      }
      case NPC_EVENT_TYPE.SCENE_FADE_IN_AND_OUT: {
        this.#isProcessingNpcEvent = true;
        // lock input, and wait for scene to fade in and out
        const fadeData = eventToHandle.data;
        // CRITICAL: #isProcessingNpcEvent (which locks the player) used to be cleared ONLY inside the
        // fadeIn(progress===1) callback — a missed fade event left the player input-locked forever.
        // A `finish` guard + a timer fallback guarantee the lock is always released and the camera is
        // never left faded out.
        let fadeDone = false;
        const finishFade = () => {
          if (fadeDone) return;
          fadeDone = true;
          this.cameras.main.resetFX();
          this.#isProcessingNpcEvent = false;
          this.#handleNpcInteraction();
        };
        this.cameras.main.fadeOut(fadeData.fadeOutDuration, 0, 0, 0, (fadeOutCamera, fadeOutProgress) => {
          if (fadeOutProgress !== 1) {
            return;
          }
          this.time.delayedCall(fadeData.waitDuration, () => {
            this.cameras.main.fadeIn(fadeData.fadeInDuration, 0, 0, 0, (fadeInCamera, fadeInProgress) => {
              if (fadeInProgress !== 1) {
                return;
              }
              finishFade();
            });
          });
        });
        // Fallback: never strand the player input-locked if a fade callback doesn't fire.
        this.time.delayedCall(
          (fadeData.fadeOutDuration ?? 0) + (fadeData.waitDuration ?? 0) + (fadeData.fadeInDuration ?? 0) + 600,
          finishFade
        );
        // TODO:future play audio cue
        break;
      }
      case NPC_EVENT_TYPE.BATTLE:
        this.#isProcessingNpcEvent = true;

        // Get monster data from the event
        const npcMonsters = eventToHandle.data.monsters.map((monsterId) => {
          return DataUtils.getMonsterById(this, monsterId);
        });
        /** @type {import('./battle-scene.js').BattleSceneData} */
        const dataToPass = {
          enemyMonsters: npcMonsters,
          playerMonsters: dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY),
          isTrainerBattle: true,
          npc: {
            id: this.#npcPlayerIsInteractingWith.id,
            assetKey: eventToHandle.data.assetKey,
            name: eventToHandle.data.trainerName,
            trainerLostMessages: eventToHandle.data.trainerLostMessages,
          },
        };
        this.#startBattleScene(dataToPass);
        break;
      default:
        exhaustiveGuard(eventType);
    }
  }

  /**
   * Creates the Phaser Zone game objects based on the `Events` tilemap data from the Tiled Map.
   * These game objects are used for creating the various in game events and cut scenes for the game.
   * The Zone game objects allow for us to check for overlaps between the player and the area that
   * is defined in the map data.
   *
   * @param {Phaser.Tilemaps.Tilemap} map
   * @returns {void}
   */
  #createEventEncounterZones(map) {
    const eventObjectLayer = map.getObjectLayer(OBJECT_LAYER_NAMES.EVENTS);
    if (!eventObjectLayer) {
      return;
    }
    const events = eventObjectLayer.objects;
    const validEvents = events.filter((event) => {
      return event.x !== undefined && event.y !== undefined;
    });

    /** @type {string[]} */
    const viewedEvents = dataManager.store.get(DATA_MANAGER_STORE_KEYS.VIEWED_EVENTS);

    for (const tiledEvent of validEvents) {
      /** @type {string} */
      const eventId = /** @type {import('../types/typedef.js').TiledObjectProperty[]} */ (tiledEvent.properties).find(
        (property) => property.name === TILED_EVENT_PROPERTY.ID
      )?.value;

      if (viewedEvents.includes(eventId)) {
        continue;
      }

      const eventZone = this.add
        .zone(tiledEvent.x, tiledEvent.y - TILE_SIZE * 2, tiledEvent.width, tiledEvent.height)
        .setOrigin(0)
        .setName(eventId);
      this.#eventZones[eventId] = eventZone;

      if (ENABLE_ZONE_DEBUGGING) {
        const debugZoneRectangle = this.add
          .rectangle(eventZone.x, eventZone.y, eventZone.width, eventZone.height, 0xff0000, 0.5)
          .setOrigin(0);
        this.#debugEventZoneObjects[eventId] = debugZoneRectangle;
      }
    }
  }

  /**
   * @returns {Promise<void>}
   */
  async #startCutScene() {
    this.#isProcessingCutSceneEvent = true;
    await /** @type {CutsceneScene} */ (this.scene.get(SCENE_KEYS.CUTSCENE_SCENE)).startCutScene();
    await sleep(500, this);
    this.#isProcessingCutSceneEvent = false;
    this.#handleCutSceneInteraction();
  }

  /**
   * @returns {Promise<void>}
   */
  async #handleCutSceneInteraction() {
    if (this.#isProcessingCutSceneEvent) {
      return;
    }
    if (this.#currentCutSceneId === undefined) {
      return;
    }

    const eventsToProcess = DataUtils.getEventData(this, this.#currentCutSceneId);

    // check to see if the cut scene has any more events to be processed
    const isMoreEventsToProcess = eventsToProcess.events.length - 1 !== this.#lastCutSceneEventHandledIndex;
    if (!isMoreEventsToProcess) {
      // once we are done processing the events for the cutscene, we need to do the following:
      //   1. update our data manager to show we watched the event
      //   2. cleanup zone game object used for the event and overlap detection
      //   3. reset our current cut scene property
      //   4. remove the cut scene bars from the scene
      this.#lastCutSceneEventHandledIndex = -1;
      this.#isProcessingCutSceneEvent = false;
      dataManager.viewedEvent(this.#currentCutSceneId);
      this.#eventZones[this.#currentCutSceneId].destroy();
      delete this.#eventZones[this.#currentCutSceneId];
      if (ENABLE_ZONE_DEBUGGING) {
        this.#gfx.clear();
        this.#debugEventZoneObjects[this.#currentCutSceneId].destroy();
        delete this.#debugEventZoneObjects[this.#currentCutSceneId];
      }
      this.#currentCutSceneId = undefined;

      await /** @type {CutsceneScene} */ (this.scene.get(SCENE_KEYS.CUTSCENE_SCENE)).endCutScene();
      await sleep(500, this);
      return;
    }

    // get the next event from the queue and process for this npc
    this.#lastCutSceneEventHandledIndex += 1;
    const eventToHandle = eventsToProcess.events[this.#lastCutSceneEventHandledIndex];
    const eventType = eventToHandle.type;

    this.#isProcessingCutSceneEvent = true;
    switch (eventType) {
      case GAME_EVENT_TYPE.ADD_NPC:
        this.#createNpcForCutScene(eventToHandle);
        break;
      case GAME_EVENT_TYPE.MOVE_TO_PLAYER:
        this.#moveNpcToPlayer(eventToHandle);
        break;
      case GAME_EVENT_TYPE.RETRACE_PATH:
        this.#haveNpcRetracePath(eventToHandle);
        break;
      case GAME_EVENT_TYPE.REMOVE_NPC:
        this.#removeNpcForCutScene(eventToHandle);
        break;
      case GAME_EVENT_TYPE.TALK_TO_PLAYER:
        this.#haveNpcTalkToPlayer(eventToHandle);
        break;
      case GAME_EVENT_TYPE.GIVE_MONSTER:
        this.#addMonsterFromNpc(eventToHandle);
        break;
      case GAME_EVENT_TYPE.ADD_FLAG:
        this.#addGameFlag(eventToHandle);
        break;
      case GAME_EVENT_TYPE.REMOVE_FLAG:
        this.#removeGameFlag(eventToHandle);
        break;
      default:
        exhaustiveGuard(eventType);
    }
  }

  /**
   * @param {import('../types/typedef.js').GameEventAddNpc} gameEvent
   * @returns {void}
   */
  #createNpcForCutScene(gameEvent) {
    const npc = new NPC({
      scene: this,
      position: { x: gameEvent.data.x * TILE_SIZE, y: gameEvent.data.y * TILE_SIZE },
      direction: gameEvent.data.direction,
      frame: gameEvent.data.frame,
      npcPath: {
        0: { x: gameEvent.data.x * TILE_SIZE, y: gameEvent.data.y * TILE_SIZE },
      },
      movementPattern: NPC_MOVEMENT_PATTERN.IDLE,
      events: [],
      animationKeyPrefix: gameEvent.data.animationKeyPrefix,
      id: gameEvent.data.id,
    });
    this.#npcs.push(npc);
    npc.addCharacterToCheckForCollisionsWith(this.#player);

    this.#isProcessingCutSceneEvent = false;
    this.#handleCutSceneInteraction();
  }

  /**
   * @param {import('../types/typedef.js').GameEventMoveToPlayer} gameEvent
   * @returns {void}
   */
  #moveNpcToPlayer(gameEvent) {
    const targetNpc = this.#npcs.find((npc) => npc.id === gameEvent.data.id);
    if (targetNpc === undefined) {
      this.#isProcessingCutSceneEvent = false;
      this.#handleCutSceneInteraction();
      return;
    }

    // determine direction to move based on distance from player
    const targetPath = getTargetPathToGameObject(targetNpc.sprite, this.#player.sprite);
    const pathToFollow = targetPath.pathToFollow.splice(0, targetPath.pathToFollow.length - 1);

    // if npc is already next to player, just update directions
    if (pathToFollow.length === 0) {
      this.#player.moveCharacter(getTargetDirectionFromGameObjectPosition(this.#player.sprite, targetNpc.sprite));
      targetNpc.facePlayer(this.#player.direction);
      this.#isProcessingCutSceneEvent = false;
      this.#handleCutSceneInteraction();
      return;
    }

    // move npc according to the path
    /** @type {import('../world/characters/npc.js').NPCPath} */
    const npcPath = {
      0: { x: targetNpc.sprite.x, y: targetNpc.sprite.y },
    };
    pathToFollow.forEach((coordinate, index) => {
      npcPath[index + 1] = coordinate;
    });

    targetNpc.finishedMovementCallback = () => {
      if (
        pathToFollow[pathToFollow.length - 1].x === targetNpc.sprite.x &&
        pathToFollow[pathToFollow.length - 1].y === targetNpc.sprite.y
      ) {
        this.#player.moveCharacter(getTargetDirectionFromGameObjectPosition(this.#player.sprite, targetNpc.sprite));
        targetNpc.facePlayer(this.#player.direction);
        this.time.delayedCall(500, () => {
          this.#isProcessingCutSceneEvent = false;
          this.#handleCutSceneInteraction();
        });
      }
    };
    targetNpc.npcMovementPattern = NPC_MOVEMENT_PATTERN.SET_PATH;
    targetNpc.npcPath = npcPath;
    targetNpc.resetMovementTime();
  }

  /**
   * @param {import('../types/typedef.js').GameEventRetracePath} gameEvent
   * @returns {void}
   */
  #haveNpcRetracePath(gameEvent) {
    const targetNpc = this.#npcs.find((npc) => npc.id === gameEvent.data.id);
    if (targetNpc === undefined) {
      this.#isProcessingCutSceneEvent = false;
      this.#handleCutSceneInteraction();
      return;
    }

    // have npc retrace their steps by reversing the existing npc path
    /** @type {import('../world/characters/npc.js').NPCPath} */
    const updatedPath = {};
    const pathKeys = Object.keys(targetNpc.npcPath).reverse();

    pathKeys.forEach((pathKey, index) => {
      updatedPath[index] = targetNpc.npcPath[pathKey];
    });

    // if npc is already next to player, there will be only 1 position in the npc path
    // when this happens, we need to just updates the npcs direction
    if (pathKeys.length === 1) {
      targetNpc.facePlayer(gameEvent.data.direction);
      this.time.delayedCall(500, () => {
        this.#isProcessingCutSceneEvent = false;
        this.#handleCutSceneInteraction();
      });
      return;
    }

    targetNpc.finishedMovementCallback = () => {
      if (
        updatedPath[pathKeys.length - 1].x === targetNpc.sprite.x &&
        updatedPath[pathKeys.length - 1].y === targetNpc.sprite.y
      ) {
        this.time.delayedCall(500, () => {
          this.#isProcessingCutSceneEvent = false;
          this.#handleCutSceneInteraction();
        });
      }
    };

    targetNpc.npcMovementPattern = NPC_MOVEMENT_PATTERN.SET_PATH;
    targetNpc.npcPath = updatedPath;
    targetNpc.resetMovementTime();
  }

  /**
   * @param {import('../types/typedef.js').GameEventRemoveNpc} gameEvent
   * @returns {void}
   */
  #removeNpcForCutScene(gameEvent) {
    // once we are done with an npc for a cutscene, we can remove that npc
    // from our npc array and then start the cleanup process of destroying the game object
    const npcToRemoveIndex = this.#npcs.findIndex((npc) => npc.id === gameEvent.data.id);
    /** @type {NPC | undefined} */
    let npcToRemove;
    if (npcToRemoveIndex !== -1) {
      // splice(i, 1) — remove ONLY this NPC. Was splice(i) with no count, which dropped EVERY NPC
      // from i onward out of the logic array (their sprites lingered as un-interactable ghosts).
      npcToRemove = this.#npcs.splice(npcToRemoveIndex, 1)[0];
    }
    this.time.delayedCall(100, () => {
      if (npcToRemove !== undefined) {
        npcToRemove.sprite.destroy();
      }
      this.#isProcessingCutSceneEvent = false;
      this.#handleCutSceneInteraction();
    });
  }

  /**
   * @param {import('../types/typedef.js').GameEventTalkToPlayer} gameEvent
   * @returns {void}
   */
  #haveNpcTalkToPlayer(gameEvent) {
    const targetNpc = this.#npcs.find((npc) => npc.id === gameEvent.data.id);
    if (targetNpc === undefined) {
      this.#isProcessingCutSceneEvent = false;
      this.#handleCutSceneInteraction();
      return;
    }

    targetNpc.isTalkingToPlayer = true;
    this.#npcPlayerIsInteractingWith = targetNpc;
    this.#dialogUi.showDialogModal(gameEvent.data.messages);
  }

  /**
   * @param {import('../types/typedef.js').GameEventGiveMonster} gameEvent
   * @returns {void}
   */
  #addMonsterFromNpc(gameEvent) {
    // TODO: add check to see if party is full and do something with 7th monster that is being added
    /** @type {import('../types/typedef.js').Monster[]} */
    const monstersInParty = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY);
    const newMonster = DataUtils.getMonsterById(this, gameEvent.data.id);
    if (newMonster) {
      newMonster.maxHp = hpAtLevel(newMonster.maxHp, newMonster.currentLevel);
      newMonster.currentHp = newMonster.maxHp;
      newMonster.currentExp = totalExpNeededForLevel(newMonster.currentLevel);
    }
    monstersInParty.push(newMonster);
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, monstersInParty);
    this.#isProcessingCutSceneEvent = false;
    this.#handleCutSceneInteraction();
  }

  /**
   * @param {import('../types/typedef.js').GameEventAddFlag} gameEvent
   * @returns {void}
   */
  #addGameFlag(gameEvent) {
    dataManager.addFlag(gameEvent.data.flag);
    this.#isProcessingCutSceneEvent = false;
    this.#handleCutSceneInteraction();
  }

  /**
   * @param {import('../types/typedef.js').GameEventRemoveFlag} gameEvent
   * @returns {void}
   */
  #removeGameFlag(gameEvent) {
    dataManager.removeFlag(gameEvent.data.flag);
    this.#isProcessingCutSceneEvent = false;
    this.#handleCutSceneInteraction();
  }

  /**
   * @param {Phaser.Tilemaps.Tilemap} map
   * @returns {void}
   */
  #createEncounterAreas(map) {
    const encounterLayers = map.getTileLayerNames().filter((layerName) => layerName.includes('Encounter'));
    if (encounterLayers.length > 0) {
      const encounterTiles = map.addTilesetImage('encounter', WORLD_ASSET_KEYS.WORLD_ENCOUNTER_ZONE);
      if (!encounterTiles) {
        console.log(
          `[${WorldScene.name}:createEncounterAreas] encountered error while creating encounter tiles from tiled`
        );
        return;
      }

      // Grass-textured encounter tiles look wrong painted over cave rock, so hide them in the Cave.
      // Detection still reads the tile DATA (not the visual), and the visible roaming mobs already
      // signal where the fights are.
      const inCave = this.#sceneData?.area?.toLowerCase() === 'cave_1';
      encounterLayers.forEach((layerName) => {
        const layer = map.createLayer(layerName, encounterTiles, 0, 0);
        layer.setAlpha(inCave ? 0 : TILED_COLLISION_LAYER_ALPHA).setDepth(2);
        this.#encounterLayers.push(layer);
      });
    }
  }

  /**
   * This method is invoked any time the player is about to start moving between grid spaces.
   * Before the player moves, check to see they will be moving into an encounter zone, and store the result.
   * We will use this result after the player finishes moving to see if there should be an encounter.
   * @param {import('../types/typedef.js').Coordinate} position
   * @returns {void}
   */
  #handlePlayerMovementStarted(position) {
    this.#encounterZonePlayerIsEntering = undefined;

    /** @type {Phaser.Tilemaps.Tile} */
    let encounterTile;
    this.#encounterLayers.some((encounterLayer) => {
      encounterTile = encounterLayer.getTileAtWorldXY(position.x, position.y, true);
      if (encounterTile === null || encounterTile.index === -1) {
        return false;
      }
      this.#encounterZonePlayerIsEntering = encounterLayer;
      return true;
    });

    if (this.#encounterZonePlayerIsEntering === undefined) {
      if (this.#player.direction === DIRECTION.DOWN) {
        // if player is moving in the down direction, hide current tile so player does not move under it
        this.#hideSpecialEncounterTiles();
      }
      return;
    }

    // check the tile type for the encounter layer the player is moving through and play related effects
    this.#handleEncounterTileTypeEffects(this.#encounterZonePlayerIsEntering, encounterTile, this.#player.direction);
  }

  /**
   * Plays the associated special effects when player is about to move through a particular tile type.
   * Example, when moving through the grass, we play a sound effect and show an additional
   * game object to make it look like the player is moving through the grass.
   *
   * @param {Phaser.Tilemaps.TilemapLayer} encounterLayer
   * @param {Phaser.Tilemaps.Tile} encounterTile
   * @param {import('../common/direction.js').Direction} playerDirection
   * @returns {void}
   */
  #handleEncounterTileTypeEffects(encounterLayer, encounterTile, playerDirection) {
    // check the tile type for the encounter layer the player is moving through and play related effects
    /** @type {import('../types/typedef.js').EncounterTileType} */
    const encounterTileType = /** @type {import('../types/typedef.js').TiledObjectProperty[]} */ (
      encounterLayer.layer.properties
    ).find((property) => property.name === TILED_ENCOUNTER_PROPERTY.TILE_TYPE).value;

    switch (encounterTileType) {
      case ENCOUNTER_TILE_TYPE.GRASS:
        // The popping grass-tuft overlay was removed — players found the tufts appearing/disappearing
        // under them as they walked distracting. The encounter still triggers; just a soft rustle, no
        // visual tuft sprite.
        playSoundFx(this, AUDIO_ASSET_KEYS.GRASS, 0.25); // soft grass rustle (was piercingly loud)
        break;
      case ENCOUNTER_TILE_TYPE.NONE:
        break;
      default:
        exhaustiveGuard(encounterTileType);
    }

    if (playerDirection !== DIRECTION.DOWN) {
      return;
    }

    // if player is moving in the down direction, hide current tile so player does not move under it
    this.#hideSpecialEncounterTiles();
  }

  /**
   * @returns {void}
   */
  #hideSpecialEncounterTiles() {
    this.#specialEncounterTileImageGameObjectGroup
      .getChildren()
      .some((/** @type {Phaser.GameObjects.Image} */ child) => {
        if (!child.active) {
          return false;
        }
        if (child.x === this.#player.sprite.x && child.y === this.#player.sprite.y) {
          child.active = false;
          child.visible = false;
          return true;
        }
        return false;
      });
  }

  /**
   * @param {string} key
   * @returns {Phaser.Tilemaps.Tilemap}
   */
  #getLevelTileMap(key) {
    if (this.#tiledLevelMaps[key]) {
      return this.#tiledLevelMaps[key];
    }
    this.#tiledLevelMaps[key] = this.make.tilemap({ key });
    return this.#tiledLevelMaps[key];
  }

  /**
   * Q-key action: find the nearest remote player and open the player-to-player interaction menu
   * (Message / Heart / Challenge) anchored to them. No-op offline / when nobody is nearby.
   * @returns {void}
   */
  #handleNearbyPlayerInteraction() {
    const client = netManager.client;
    if (!client || !client.online) {
      return;
    }
    // toggle: pressing the interact key again closes an open menu
    if (this.#interactionMenu?.isOpen) {
      this.#interactionMenu.close();
      this.#menuTarget = null;
      return;
    }
    const target = this.#remotePlayers?.nearest(this.#player.sprite.x, this.#player.sprite.y, 160);
    if (!target) {
      this.#chatOverlay?.addSystem('No player nearby to interact with.');
      return;
    }
    const sprite = this.#remotePlayers?.ref(target.sessionId)?.sprite;
    if (!sprite) return;
    this.#openInteractionMenu(target.sessionId, target.name, sprite);
  }

  /**
   * Open the interaction menu anchored above a remote player's sprite. Robust to a vanished
   * sprite (no-op). The menu position is refreshed every frame in `update` while it is open.
   * @param {string} sessionId
   * @param {string} name
   * @param {Phaser.GameObjects.Sprite} sprite
   * @returns {void}
   */
  /**
   * Snapshot for the minimap radar: the local player's world position, the map size, and every remote
   * player's position (real players + ambient bots). Returns null while the player isn't ready.
   * @returns {{ px: number, py: number, mapW: number, mapH: number, others: {x:number,y:number}[] } | null}
   */
  getRadarSnapshot() {
    const sprite = this.#player?.sprite;
    if (!sprite?.active) return null;
    return {
      px: sprite.x,
      py: sprite.y,
      mapW: this.#worldMapPixelW || 1,
      mapH: this.#worldMapPixelH || 1,
      others: this.#remotePlayers?.positions() ?? [],
    };
  }

  /**
   * Open the interaction menu (Message / Heart / Challenge) for a FRIEND who is currently in this
   * area, found by username. Lets players duel a friend without knowing their wallet. Returns false
   * (and toasts) if the friend isn't in this area, so the caller can suggest using Join first.
   * @param {string} username
   * @returns {boolean}
   */
  challengeFriendByName(username) {
    const ref = this.#remotePlayers?.findByName(username);
    if (!ref) {
      this.#interactionMenu?.toast(`${username} isn't in your area — use Join to spawn with them first.`, '#f5b23e');
      return false;
    }
    this.#openInteractionMenu(ref.sessionId, ref.name, ref.sprite);
    return true;
  }

  #openInteractionMenu(sessionId, name, sprite) {
    if (!this.#interactionMenu || !sprite?.active) return;
    const anchor = this.#worldToPage(sprite.x, sprite.y - 28);
    this.#menuTarget = { sessionId };
    this.#interactionMenu.open({ sessionId, name }, anchor.x, anchor.y);
  }

  /**
   * Convert a WORLD coordinate to a PAGE (CSS pixel) coordinate, so a DOM overlay can be anchored
   * over an in-world sprite. Accounts for the main camera scroll + zoom and the canvas's on-page
   * rect under Phaser's RESIZE scale mode.
   * @param {number} worldX
   * @param {number} worldY
   * @returns {{ x: number, y: number }}
   */
  #worldToPage(worldX, worldY) {
    const cam = this.cameras.main;
    // world → camera screen space (game resolution pixels)
    const sx = (worldX - cam.worldView.x) * cam.zoom;
    const sy = (worldY - cam.worldView.y) * cam.zoom;
    // game resolution pixels → page pixels via the canvas rect (RESIZE mode ≈ 1:1, but the
    // bounding-rect ratio keeps us correct under any devicePixelRatio / CSS scaling).
    const canvas = this.game.canvas;
    const rect = canvas.getBoundingClientRect();
    const ratioX = rect.width / this.scale.width;
    const ratioY = rect.height / this.scale.height;
    return { x: rect.left + sx * ratioX, y: rect.top + sy * ratioY };
  }

  /**
   * ARENA menu action: join the consensual ranked-PvP queue. The server replies with a
   * `system` "Queued…" message and, when matched, a `battleStart`. No-op offline.
   * @returns {void}
   */
  #handleArenaQueue() {
    const client = netManager.client;
    if (!client || !client.online) {
      this.#dialogUi?.showDialogModal(['The Arena is only available while connected to a realm.']);
      return;
    }
    if (this.#arenaQueued) {
      this.#chatOverlay?.addSystem('Already searching for an Arena opponent…');
      return;
    }
    this.#arenaQueued = true;
    client.arenaQueue();
    this.#chatOverlay?.addSystem('🏟️ Searching for an Arena opponent…');
    this.#dialogUi?.showDialogModal(['Searching for an Arena opponent…']);
    // safety timeout: if we're never matched, dismiss the dialog and clear the queued flag so the
    // player can retry instead of being stuck on a perpetual "Searching…" modal.
    this.time.delayedCall(15000, () => {
      if (this.#arenaQueued) {
        this.#arenaQueued = false;
        if (this.#dialogUi?.isVisible) {
          this.#dialogUi.hideDialogModal();
        }
      }
    });
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Public menu/scene-launch API.
  //
  // These wrap the exact launch+pause behavior the keyboard WorldMenu uses, so the
  // mouse/touch WorldHudScene can trigger the SAME flows (open over the world, pause the
  // world so the player returns where they were, resume on close). Both input paths share
  // one implementation — there is a single source of truth for how each panel opens.
  // ──────────────────────────────────────────────────────────────────────────────

  /**
   * Grant a fresh starter when the party is empty, so a wiped/lost account is never stranded with no
   * monsters (and can't battle). Persists immediately. Idempotent — a no-op once the party has at
   * least one monster, so it never overwrites a real team.
   * @returns {void}
   */
  #ensurePartyNotEmpty() {
    const party = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY);
    if (Array.isArray(party) && party.length > 0) return;
    const options = getStarterOptions();
    const starter = options.length ? buildStarterMonster(this, options[0].speciesId) : undefined;
    if (!starter) return;
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, [starter]);
    dataManager.saveData();
  }

  /**
   * Persist the current party to the server (autosave). No-op without a wallet or for an empty party
   * (the server also rejects empty, so a transient/empty state can never wipe a saved team).
   * Fire-and-forget.
   * @returns {void}
   */
  #autosaveParty() {
    const party = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY);
    if (!Array.isArray(party) || party.length === 0) return;
    // Local persistence first (covers the post-battle party write, which the battle scene set in the
    // store but never saved — that was the localStorage half of the "lost beasts on refresh" bug).
    dataManager.saveData();
    // Then the durable server copy — but ONLY for a fresh, server-confirmed account. If we hydrated
    // from the offline cache (server was unreachable), the loaded party may be stale, so we must NOT
    // write it back over a potentially-newer server party.
    if (netManager.wallet && !netManager.partyFromCacheOnly) {
      // Persist the PC box too, so monsters caught with a full party survive a refresh / new device.
      // Only persist the PC box when it has contents — the periodic autosave must NEVER overwrite a
      // non-empty server box with an empty array (a transient empty client box would wipe stored beasts).
      // The box only grows here (caught-while-full); emptying it is a deliberate box-scene action.
      const storage = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_STORAGE);
      const storageArg = Array.isArray(storage) && storage.length > 0 ? storage : undefined;
      void saveParty(netManager.wallet, party, storageArg).catch(() => {});
    }
  }

  /**
   * Open an overlay panel as the SINGLE active panel: first close any OTHER open panel so they never
   * stack/overlap (the "Cards menu won't close + steals clicks" bug — which also made Ranks need
   * several taps because a stale panel sat over its button), then launch the target and pause the
   * world. Re-opening the same panel closes + relaunches it cleanly.
   * @param {string} sceneKey
   * @param {object} [data]
   * @returns {void}
   */
  #openOverlay(sceneKey, data) {
    const OVERLAY_KEYS = [
      SCENE_KEYS.PROFILE_SCENE,
      SCENE_KEYS.MONSTER_PARTY_SCENE,
      SCENE_KEYS.PC_BOX_SCENE,
      SCENE_KEYS.INVENTORY_SCENE,
      SCENE_KEYS.QUESTS_SCENE,
      SCENE_KEYS.MARKETPLACE_SCENE,
      SCENE_KEYS.CARD_SHOP_SCENE,
      SCENE_KEYS.LEADERBOARD_SCENE,
      SCENE_KEYS.FRIENDS_SCENE,
      SCENE_KEYS.MAP_SCENE,
      SCENE_KEYS.HOW_TO_PLAY_SCENE,
      SCENE_KEYS.VAULT_SCENE,
      SCENE_KEYS.SETTINGS_SCENE,
    ];
    for (const k of OVERLAY_KEYS) {
      if (this.scene.isActive(k) || this.scene.isPaused(k)) this.scene.stop(k);
    }
    this.scene.launch(sceneKey, data ?? { previousSceneName: SCENE_KEYS.WORLD_SCENE });
    this.scene.pause(SCENE_KEYS.WORLD_SCENE);
    // CRITICAL: the HUD scenes were brought to the top of the scene stack on world entry, so a freshly
    // launched overlay renders BELOW them — and with global-top-only input the HUD's buttons/minimap
    // steal clicks meant for the overlay (the "buttons hard to click / inconsistent hit" bug). Bring
    // the overlay above the HUD so its buttons are always the topmost thing under the pointer.
    this.scene.bringToTop(sceneKey);
  }

  /**
   * Open the Monster Party scene. No-op (with a dialog) when the party is empty, mirroring the
   * keyboard menu's guard.
   * @returns {void}
   */
  openParty() {
    if (dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY).length === 0) {
      this.#dialogUi.showDialogModal(['You have no monsters in your party.']);
      return;
    }
    /** @type {import('./monster-party-scene.js').MonsterPartySceneData} */
    const sceneDataToPass = { previousSceneName: SCENE_KEYS.WORLD_SCENE };
    this.#openOverlay(SCENE_KEYS.MONSTER_PARTY_SCENE, sceneDataToPass);
  }

  /**
   * Open the PC Box — store/withdraw beasts beyond the 6-slot party.
   * @returns {void}
   */
  openPcBox() {
    this.#openOverlay(SCENE_KEYS.PC_BOX_SCENE, { previousSceneName: SCENE_KEYS.WORLD_SCENE });
  }

  /**
   * Open the Bag / Inventory scene.
   * @returns {void}
   */
  openInventory() {
    /** @type {import('./inventory-scene.js').InventorySceneData} */
    const sceneDataToPass = {
      previousSceneName: SCENE_KEYS.WORLD_SCENE,
      itemCategoriesThatCannotBeUsed: [ITEM_CATEGORY.CAPTURE],
    };
    this.#openOverlay(SCENE_KEYS.INVENTORY_SCENE, sceneDataToPass);
  }

  /**
   * Open the daily Quests scene.
   * @returns {void}
   */
  openQuests() {
    this.#openOverlay(SCENE_KEYS.QUESTS_SCENE);
  }

  /**
   * Open the Marketplace scene.
   * @returns {void}
   */
  openMarket() {
    this.#openOverlay(SCENE_KEYS.MARKETPLACE_SCENE);
  }

  /**
   * Open the Profile scene.
   * @returns {void}
   */
  openProfile() {
    this.#openOverlay(SCENE_KEYS.PROFILE_SCENE);
  }

  /**
   * Toggle the keyboard-style WorldMenu (the ☰ "more" button). Does nothing while a dialog or
   * npc event is in progress so the mouse path matches the keyboard ENTER guard.
   * @returns {void}
   */
  openMenu() {
    if (this.#dialogUi?.isVisible || this.#isProcessingNpcEvent || this.#player?.isMoving) {
      return;
    }
    if (this.#menu.isVisible) {
      this.#menu.hide();
      return;
    }
    this.#menu.show();
  }

  /**
   * Open the premium Card Shop overlay (redeem Gold for real graded cards). Pauses the world
   * so the player returns exactly where they were; the shop degrades gracefully offline.
   * @returns {void}
   */
  openCardShop(view) {
    this.#openOverlay(SCENE_KEYS.CARD_SHOP_SCENE, {
      previousSceneName: SCENE_KEYS.WORLD_SCENE,
      ...(view ? { view } : {}),
    });
  }

  /**
   * The VAULT menu button opens the LIVE Card Vault — stake a real graded-card NFT to earn passive
   * Gold. That UI is the Card Shop's VAULT tab, so open the shop directly on it. (The legacy on-chain
   * beasts vault-scene is paused and no longer reachable from the menu.)
   * @returns {void}
   */
  openVault() {
    this.openCardShop('vault');
  }

  /**
   * Open the Map / area panel (current area + stylized region minimap). Mouse-driven.
   * @returns {void}
   */
  openMap() {
    this.#openOverlay(SCENE_KEYS.MAP_SCENE);
  }

  /**
   * Open the Leaderboard overlay (top players by Gold). Mouse-driven; pauses the world so the
   * player returns exactly where they were, and degrades gracefully offline (empty board).
   * @returns {void}
   */
  openLeaderboard() {
    this.#openOverlay(SCENE_KEYS.LEADERBOARD_SCENE);
  }

  /**
   * Open the Friends & Invites overlay (friends list + presence, requests, add-by-name, invite
   * link). Mouse-driven; pauses the world so the player returns where they were, and degrades
   * gracefully offline (empty state).
   * @returns {void}
   */
  openFriends() {
    this.#openOverlay(SCENE_KEYS.FRIENDS_SCENE);
  }

  /**
   * Open the "How to Play" onboarding overlay (the core loop + controls). Mouse/touch-driven;
   * pauses the world so the player returns exactly where they were. Reopenable any time from the
   * HUD; also auto-shown once to new players on first world entry (see create()).
   * @returns {void}
   */
  openHowToPlay() {
    this.#openOverlay(SCENE_KEYS.HOW_TO_PLAY_SCENE);
  }

  /**
   * Open the mouse-driven Settings overlay (audio: master / music / sfx / volume). Applies +
   * persists immediately, resumes the world on close.
   * @returns {void}
   */
  openSettings() {
    this.#openOverlay(SCENE_KEYS.SETTINGS_SCENE);
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Mouse / touch input: tap-to-move + clickable NPCs, signs, and roaming mobs.
  // ──────────────────────────────────────────────────────────────────────────────

  /**
   * Registers the single world-level pointer handler. Clickable game objects (npcs, signs,
   * mobs) are made interactive individually and stop propagation so a click on them does NOT
   * also trigger tap-to-move. A bare click on the ground walks the player toward that tile.
   * @returns {void}
   */
  #setupPointerInput() {
    // Free click/tap-to-move on open ground is DISABLED — overworld movement is keyboard/arrows
    // only (the user asked for this). Clicking an NPC / sign / roaming mob still works: those are
    // targeted interactions (handled by their own GAMEOBJECT_POINTER_DOWN handlers, which walk the
    // player to the target via #approachTile/#approachMob). So no ground POINTER_DOWN handler here.
  }

  /**
   * Whether a screen-space pointer lands in a HUD-reserved band so a button/panel press never
   * also walks the player. Mirrors the Monstara HUD layout (world-hud-scene.js + currency-hud):
   *   • top-left   → player card (~278x124)
   *   • left edge  → vertical icon menu column (~70px wide, top→mid screen)
   *   • top-right  → location/time/Gold panel (~260x124)
   *   • bottom     → party bar + current-quest panel strip (~130px)
   * Conservative bands keep the world fully tappable everywhere else.
   * @param {number} sx screen x
   * @param {number} sy screen y
   * @returns {boolean}
   */
  #isPointerOverHud(sx, sy) {
    const w = this.scale.width;
    const h = this.scale.height;
    // bottom strip: party chips (bottom-left) + current-quest panel (bottom-right)
    if (sy >= h - 130) {
      return true;
    }
    // top-left player card and top-right location/Gold panel
    if (sy <= 124 && (sx <= 292 || sx >= w - 260)) {
      return true;
    }
    // left vertical icon menu column (sits under the player card; now 8 icon buttons tall —
    // card ≈124 start + 8 × ~66px ≈ 680. Reserve down to that, but never past the bottom party
    // strip, so a button press never also walks the player.
    if (sx <= 76 && sy <= Math.min(680, h - 130)) {
      return true;
    }
    return false;
  }

  /**
   * Click/tap on open world: snap the target to the tile grid and queue a step-by-step path
   * toward it (greedy, collision-aware). Keyboard movement still works and simply overrides the
   * queue on the next frame.
   * @param {number} worldX
   * @param {number} worldY
   * @returns {void}
   */
  #handleWorldTap(worldX, worldY) {
    const targetTileX = Math.floor(worldX / TILE_SIZE) * TILE_SIZE;
    const targetTileY = Math.floor(worldY / TILE_SIZE) * TILE_SIZE;
    this.#moveQueue = this.#buildPathToTile(
      { x: this.#player.sprite.x, y: this.#player.sprite.y },
      { x: targetTileX, y: targetTileY }
    );
  }

  /**
   * Greedy collision-aware path builder on the tile grid: at each step move along the axis with
   * the greater remaining distance, falling back to the other axis when blocked. Capped so a
   * far/blocked tap can never spin. Good enough for "walk over there" without a full BFS.
   * @param {import('../types/typedef.js').Coordinate} from grid-aligned start
   * @param {import('../types/typedef.js').Coordinate} to grid-aligned target
   * @returns {import('../common/direction.js').Direction[]}
   */
  #buildPathToTile(from, to) {
    /** @type {import('../common/direction.js').Direction[]} */
    const path = [];
    let cur = { x: from.x, y: from.y };
    const maxSteps = 64;

    for (let i = 0; i < maxSteps; i += 1) {
      if (cur.x === to.x && cur.y === to.y) {
        break;
      }
      const dx = to.x - cur.x;
      const dy = to.y - cur.y;
      // primary axis = whichever has the larger remaining distance
      /** @type {import('../common/direction.js').Direction[]} */
      const candidates = [];
      if (Math.abs(dx) >= Math.abs(dy)) {
        if (dx !== 0) candidates.push(dx > 0 ? DIRECTION.RIGHT : DIRECTION.LEFT);
        if (dy !== 0) candidates.push(dy > 0 ? DIRECTION.DOWN : DIRECTION.UP);
      } else {
        if (dy !== 0) candidates.push(dy > 0 ? DIRECTION.DOWN : DIRECTION.UP);
        if (dx !== 0) candidates.push(dx > 0 ? DIRECTION.RIGHT : DIRECTION.LEFT);
      }

      let advanced = false;
      for (const dir of candidates) {
        const next = getTargetPositionFromGameObjectPositionAndDirection(cur, dir);
        if (!this.#isTileBlocked(next)) {
          path.push(dir);
          cur = next;
          advanced = true;
          break;
        }
      }
      // fully blocked toward the target — stop here (player walks as far as possible)
      if (!advanced) {
        break;
      }
    }
    return path;
  }

  /**
   * Whether a grid position is blocked by the collision layer (mirrors the Character collision
   * check used by the actual movement, so a queued step never desyncs from real movement).
   * @param {import('../types/typedef.js').Coordinate} position
   * @returns {boolean}
   */
  #isTileBlocked(position) {
    if (!this.#collisionLayer) {
      return false;
    }
    const tile = this.#collisionLayer.getTileAtWorldXY(position.x, position.y, true);
    return tile != null && tile.index !== -1;
  }

  /**
   * Keep a REMOTE player/bot sprite ON the walkable map. If its target lands on a solid tile (a laggy
   * player position, or an ambient bot whose server-side random walk doesn't know the collision map),
   * snap it to the nearest walkable tile so remotes never render inside walls/water/trees. Returns the
   * position unchanged when it's already walkable, so normal on-path movement stays smooth.
   * @param {number} x @param {number} y @returns {{x:number,y:number}}
   */
  #snapRemoteToPath(x, y) {
    if (!this.#collisionLayer) return { x, y };
    // sample the sprite's FEET (same offset the local player uses) — that's the tile it stands on.
    const fx = x + TILE_SIZE / 2;
    const fy = y + TILE_SIZE * 0.6;
    if (!this.#isTileBlocked({ x: fx, y: fy })) return { x, y };
    const baseTx = Math.floor(fx / TILE_SIZE);
    const baseTy = Math.floor(fy / TILE_SIZE);
    let best = null;
    let bestD = Infinity;
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        if (dx === 0 && dy === 0) continue;
        const cx = (baseTx + dx) * TILE_SIZE + TILE_SIZE / 2;
        const cy = (baseTy + dy) * TILE_SIZE + TILE_SIZE / 2;
        if (this.#isTileBlocked({ x: cx, y: cy })) continue;
        const d = dx * dx + dy * dy;
        if (d < bestD) {
          bestD = d;
          best = { tx: baseTx + dx, ty: baseTy + dy };
        }
      }
    }
    if (!best) return { x, y }; // boxed in by walls — leave as-is rather than teleport far
    return { x: best.tx * TILE_SIZE, y: best.ty * TILE_SIZE };
  }

  /**
   * Makes every NPC sprite clickable: clicking walks the player to a tile adjacent to the NPC
   * (if not already adjacent) and then runs the exact same interaction as SPACE-when-facing.
   * Stops propagation so the click does not also trigger ground tap-to-move.
   * @returns {void}
   */
  #makeNpcsClickable() {
    this.#npcs.forEach((npc) => {
      npc.sprite.setInteractive({ useHandCursor: true });
      npc.sprite.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, (/** @type {Phaser.Input.Pointer} */ pointer, _x, _y, event) => {
        event?.stopPropagation?.();
        if (this.#isPlayerInputLocked() || this.#wildMonsterEncountered) {
          return;
        }
        this.#approachAndInteractWithNpc(npc);
      });
    });
  }

  /**
   * Makes sign objects clickable: walks the player just below the sign (signs are read from
   * below, facing UP) and then triggers the same dialog as the keyboard sign interaction.
   * @param {Phaser.Tilemaps.Tilemap} map
   * @returns {void}
   */
  #makeSignsClickable(map) {
    if (!this.#signLayer) {
      return;
    }
    this.#signLayer.objects.forEach((object) => {
      if (!object.x || !object.y) {
        return;
      }
      // sign top-left tile position in world space (Tiled y is the object's bottom)
      const signX = object.x;
      const signY = object.y - TILE_SIZE;
      const zone = this.add
        .zone(signX, signY, TILE_SIZE, TILE_SIZE)
        .setOrigin(0)
        .setInteractive({ useHandCursor: true });
      zone.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, (/** @type {Phaser.Input.Pointer} */ pointer, _x, _y, event) => {
        event?.stopPropagation?.();
        if (this.#isPlayerInputLocked() || this.#wildMonsterEncountered) {
          return;
        }
        // approach the tile directly below the sign, face UP, then read it
        this.#approachTile({ x: signX, y: signY + TILE_SIZE }, DIRECTION.UP, () => {
          this.#handlePlayerInteraction();
        });
      });
    });
  }

  /**
   * Walk the player to a tile adjacent to the given NPC, face it, then run the standard NPC
   * interaction. If already adjacent, interacts immediately.
   * @param {NPC} npc
   * @returns {void}
   */
  #approachAndInteractWithNpc(npc) {
    const px = this.#player.sprite.x;
    const py = this.#player.sprite.y;
    const nx = npc.sprite.x;
    const ny = npc.sprite.y;
    const dist = Math.abs(nx - px) + Math.abs(ny - py);

    const interact = () => {
      // face the npc based on the player's relative position, then trigger interaction
      /** @type {import('../common/direction.js').Direction} */
      let dir = DIRECTION.DOWN;
      if (ny < this.#player.sprite.y) dir = DIRECTION.UP;
      else if (ny > this.#player.sprite.y) dir = DIRECTION.DOWN;
      else if (nx < this.#player.sprite.x) dir = DIRECTION.LEFT;
      else if (nx > this.#player.sprite.x) dir = DIRECTION.RIGHT;
      this.#player.moveCharacter(dir); // turn to face (blocked by npc, so just turns)
      npc.facePlayer(dir);
      npc.isTalkingToPlayer = true;
      this.#npcPlayerIsInteractingWith = npc;
      this.#handleNpcInteraction();
    };

    if (dist <= TILE_SIZE) {
      interact();
      return;
    }

    // pick the reachable adjacent tile closest to the player and walk there, then interact
    const adjacentTiles = [
      { x: nx - TILE_SIZE, y: ny },
      { x: nx + TILE_SIZE, y: ny },
      { x: nx, y: ny - TILE_SIZE },
      { x: nx, y: ny + TILE_SIZE },
    ].filter((t) => !this.#isTileBlocked(t));
    if (adjacentTiles.length === 0) {
      return;
    }
    adjacentTiles.sort(
      (a, b) =>
        Math.abs(a.x - px) + Math.abs(a.y - py) - (Math.abs(b.x - px) + Math.abs(b.y - py))
    );
    this.#approachTile(adjacentTiles[0], undefined, interact);
  }

  /**
   * Queue a tap-to-move path to a target tile and run a callback once the player arrives. Used
   * by clickable NPCs/signs so the click first walks the player into interaction range.
   * @param {import('../types/typedef.js').Coordinate} tile grid-aligned destination
   * @param {import('../common/direction.js').Direction | undefined} faceDir optional facing on arrival
   * @param {() => void} onArrive
   * @returns {void}
   */
  #approachTile(tile, faceDir, onArrive) {
    this.#moveQueue = this.#buildPathToTile({ x: this.#player.sprite.x, y: this.#player.sprite.y }, tile);
    if (this.#moveQueue.length === 0) {
      if (faceDir) {
        this.#player.moveCharacter(faceDir);
      }
      onArrive();
      return;
    }
    // poll until the queue drains and the player is idle, then face + fire the callback
    const tick = () => {
      if (this.#moveQueue.length === 0 && !this.#player.isMoving) {
        if (faceDir) {
          this.#player.moveCharacter(faceDir);
        }
        onArrive();
        return;
      }
      this.time.delayedCall(120, tick);
    };
    this.time.delayedCall(120, tick);
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Visible roaming mobs.
  // ──────────────────────────────────────────────────────────────────────────────

  /**
   * Spawns a small pool of visible creature sprites on walkable grass tiles inside the
   * `Encounter-Area-N` zones. Walking adjacent to / overlapping a mob, or clicking it, starts a
   * battle against that area's encounter. Mobs respawn on the next world create() (after a
   * battle the world scene restarts), so the pool naturally refreshes.
   * @param {Phaser.Tilemaps.Tilemap} map
   * @returns {void}
   */
  #spawnRoamingMobs(map) {
    this.#mobs = [];
    if (this.#encounterLayers.length === 0) {
      return;
    }

    const MAX_MOBS = isEventActive() ? 26 : 16; // denser roaming creatures (more during the special event)
    const MOBS_PER_LAYER = 6;

    for (const layer of this.#encounterLayers) {
      if (this.#mobs.length >= MAX_MOBS) {
        break;
      }
      // the encounter area id this layer maps to (encounters.json area flow)
      const encounterAreaId = /** @type {import('../types/typedef.js').TiledObjectProperty[]} */ (
        layer.layer.properties
      ).find((property) => property.name === TILED_ENCOUNTER_PROPERTY.AREA)?.value;
      if (encounterAreaId === undefined) {
        continue;
      }

      // the weighted species pool for this encounter area; each mob rolls one species so the
      // visible creature, its sprite, and the battle monster are all the SAME species.
      const possibleMonsters = DataUtils.getEncounterAreaDetails(this, encounterAreaId);
      if (!possibleMonsters || possibleMonsters.length === 0) {
        continue;
      }

      // collect walkable encounter tiles in this layer (a tile that exists in the encounter
      // layer AND is not blocked by collision)
      /** @type {import('../types/typedef.js').Coordinate[]} */
      const spawnTiles = [];
      layer.forEachTile((tile) => {
        if (tile.index === -1) {
          return;
        }
        const pos = { x: tile.pixelX, y: tile.pixelY };
        if (this.#isTileBlocked(pos)) {
          return;
        }
        spawnTiles.push(pos);
      });
      if (spawnTiles.length === 0) {
        continue;
      }

      const count = Math.min(MOBS_PER_LAYER, spawnTiles.length, MAX_MOBS - this.#mobs.length);
      for (let i = 0; i < count; i += 1) {
        const tile = spawnTiles[Phaser.Math.Between(0, spawnTiles.length - 1)];
        // roll the specific species this mob IS, and render it with its OWN sprite
        const monsterId = weightedRandom(possibleMonsters);
        const monster = DataUtils.getMonsterById(this, monsterId);
        if (!monster) {
          continue;
        }
        // Normalize EVERY overworld mob to the same on-map size (~80px ≈ 1.25 tiles) from its ACTUAL
        // source resolution — the old assetScale heuristic left big-source sprites huge (Solani's
        // source is 739×901 → 0.3× was still ~222px). Target / sourceWidth reproduces the old good
        // scales (256px→0.31, 80px→1.0) while shrinking oversized art to match.
        const TARGET_MAP_PX = 80;
        const srcImg = this.textures.get(monster.assetKey)?.getSourceImage?.();
        const srcW = srcImg && srcImg.width ? srcImg.width : 256;
        const textureScale = Phaser.Math.Clamp(TARGET_MAP_PX / srcW, 0.05, 2);
        const mob = new RoamingMob({
          scene: this,
          position: { x: tile.x, y: tile.y },
          texture: monster.assetKey,
          textureScale,
          encounterAreaId,
          monsterId,
          isBlockedFn: (p) => this.#isTileBlocked(p),
          // clicking the mob walks the player toward it (tap-to-move); the battle only triggers on
          // contact/adjacency via #checkMobCollision, so you can't fight a mob from across the map.
          onClick: (m) => this.#approachMob(m),
        });
        this.#mobs.push(mob);
      }
    }
  }

  /**
   * Click/tap a roaming mob: walk the player toward a tile adjacent to it (reusing the tap-to-move
   * path logic). Does NOT start the battle — the existing bump/adjacency check (#checkMobCollision,
   * run after each movement step) triggers the fight once the player actually reaches it. If already
   * adjacent, the bump check will fire on the next movement update.
   * @param {import('../world/roaming-mob.js').RoamingMob} mob
   * @returns {void}
   */
  #approachMob(mob) {
    if (this.#wildMonsterEncountered || this.#isPlayerInputLocked()) {
      return;
    }
    // the mob sprite is centered on its tile (origin 0.5), so derive the tile-aligned top-left
    // (matching how the tile-aligned player positions are addressed) before computing neighbors.
    const mobTileX = Math.round((mob.sprite.x - TILE_SIZE / 2) / TILE_SIZE) * TILE_SIZE;
    const mobTileY = Math.round((mob.sprite.y - TILE_SIZE / 2) / TILE_SIZE) * TILE_SIZE;
    // pick the reachable adjacent tile closest to the player and walk there
    const adjacentTiles = [
      { x: mobTileX - TILE_SIZE, y: mobTileY },
      { x: mobTileX + TILE_SIZE, y: mobTileY },
      { x: mobTileX, y: mobTileY - TILE_SIZE },
      { x: mobTileX, y: mobTileY + TILE_SIZE },
    ].filter((t) => !this.#isTileBlocked(t));
    if (adjacentTiles.length === 0) {
      return;
    }
    const px = this.#player.sprite.x;
    const py = this.#player.sprite.y;
    adjacentTiles.sort(
      (a, b) => Math.abs(a.x - px) + Math.abs(a.y - py) - (Math.abs(b.x - px) + Math.abs(b.y - py))
    );
    // queue the walk; #checkMobCollision (run on each movement-finished) starts the battle on contact
    this.#moveQueue = this.#buildPathToTile({ x: px, y: py }, adjacentTiles[0]);
  }

  /**
   * Starts a battle versus the SPECIFIC mob the player engaged. Uses the mob's own monster identity
   * (chosen at spawn) so the battle sprite + name + stats exactly match the creature on the map.
   * @param {import('../world/roaming-mob.js').RoamingMob} mob
   * @returns {void}
   */
  #startMobBattle(mob) {
    if (this.#wildMonsterEncountered || this.#isPlayerInputLocked()) {
      return;
    }

    const enemyMonster = DataUtils.getMonsterById(this, mob.monsterId);
    if (!enemyMonster) {
      return;
    }
    this.#wildMonsterEncountered = true;
    // server-authoritative reward when available; keeps THIS mob's species/sprite as the enemy
    // (the visible-mob == battle-monster invariant) and only adopts the session's reward level.
    // Falls back to the fully-local battle below on any api failure.
    void this.#startMobBattleWithSession(mob, enemyMonster);
  }

  /**
   * Open a single-use server battle session for a roaming-mob fight, then start the battle. The
   * enemy stays the mob's own species (visual invariant); when a session is granted we adopt its
   * reward-bearing level + pass the sessionId so the win/capture reward is server-authoritative.
   * Degrades to the legacy fully-local battle (no session id) on ANY api failure. Never throws.
   * @param {import('../world/roaming-mob.js').RoamingMob} mob
   * @param {import('../types/typedef.js').Monster} enemyMonster the mob's own battle monster
   * @returns {Promise<void>}
   */
  async #startMobBattleWithSession(mob, enemyMonster) {
    /** @type {string | undefined} */
    let battleSessionId;
    try {
      const session = await startBattleSession(netManager.wallet, mob.encounterAreaId);
      if (session) {
        battleSessionId = session.sessionId;
        // adopt the server's reward level so the local reward estimate + server reward agree;
        // baked battle stats are untouched, so damage/state-machine balance is identical.
        enemyMonster.currentLevel = session.level;
      }
    } catch (err) {
      console.warn('[battle] mob session start failed — using local battle', err);
    }

    // scale the mob enemy's HP to its level (same as wild encounters) so its level is meaningful
    enemyMonster.maxHp = hpAtLevel(enemyMonster.maxHp, enemyMonster.currentLevel);
    enemyMonster.currentHp = enemyMonster.maxHp;
    enemyMonster.currentExp = totalExpNeededForLevel(enemyMonster.currentLevel);

    /** @type {import('./battle-scene.js').BattleSceneData} */
    const dataToPass = {
      enemyMonsters: [enemyMonster],
      playerMonsters: dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY),
      // realm/area let the battle scene pick its backdrop (Cave → cavern, else grassland arena).
      realm: this.#sceneData.realm,
      area: this.#sceneData.area,
    };
    if (battleSessionId) {
      dataToPass.battleSessionId = battleSessionId;
    }
    this.#startBattleScene(dataToPass);
  }

  /**
   * After the player finishes a step, check whether they ended up adjacent to / on top of a
   * roaming mob and, if so, start that mob's battle. Called from the movement-finished handler.
   * @returns {void}
   */
  #checkMobCollision() {
    if (this.#wildMonsterEncountered || this.#mobs.length === 0) {
      return;
    }
    const { x, y } = this.#player.sprite;
    const mob = this.#mobs.find((m) => {
      const mx = m.sprite.x;
      const my = m.sprite.y;
      const dist = Math.abs(mx - x) + Math.abs(my - y);
      return dist <= TILE_SIZE; // same tile or one tile away (4-neighborhood)
    });
    if (mob) {
      this.#startMobBattle(mob);
    }
  }

  /**
   * Transitions to the BattleScene and passes along the provided data.
   * @param {import('./battle-scene.js').BattleSceneData} battleSceneData
   */
  #startBattleScene(battleSceneData) {
    // Start the battle IMMEDIATELY and SYNCHRONOUSLY. The old code faded the camera out and only
    // started the battle on the camera FADE_OUT_COMPLETE event (or a scene-clock timer) — but both
    // depend on the world scene's update loop, which was getting stuck, so the world stayed faded to
    // BLACK with the HUD up and the battle never started (the "black screen entering a battle" bug).
    // scene.start is a global SceneManager op and never depends on the camera fade, so this can't
    // strand the player. (A browser-timer fallback guards against any throw on the synchronous path.)
    try {
      this.scene.start(SCENE_KEYS.BATTLE_SCENE, battleSceneData);
    } catch (err) {
      console.error('[battle] synchronous start failed, retrying', err);
      window.setTimeout(() => this.scene.start(SCENE_KEYS.BATTLE_SCENE, battleSceneData), 50);
    }
  }

  /**
   * Open the dedicated, server-authoritative PvP battle scene in response to a `battleStart`
   * server message. Both players' real lead monsters are carried in the payload (the server
   * derived them from each party); the PvP scene is a pure view over the synced duel and never
   * computes damage. We *pause* the world scene (don't stop it) so multiplayer stays wired and
   * we can resume cleanly on `battleEnd`.
   * @param {Extract<import('@mn/shared').ServerMessage, { t: 'battleStart' }>} message
   * @returns {void}
   */
  #startPvpBattle(message) {
    this.#chatOverlay?.addSystem(
      message.kind === 'arena' ? '⚔️ Arena match found!' : '⚔️ Duel started!',
    );

    // Heal items the player owns (for the in-battle ITEM menu). Capture items are intentionally
    // excluded — you can't capture another player's monster. ids match the server's HEAL_BY_ITEM.
    const HEAL_ITEM_IDS = ['it_potion', 'it_greater_potion', 'it_revive'];
    const bag = (dataManager.getInventory(this) || [])
      .filter((slot) => slot?.item && HEAL_ITEM_IDS.includes(slot.item.id) && (slot.quantity ?? 0) > 0)
      .map((slot) => ({ itemId: slot.item.id, name: slot.item.name ?? slot.item.id, qty: slot.quantity }));

    /** @type {import('./pvp-battle-scene.js').PvpBattleSceneData} */
    const dataToPass = {
      battleId: message.battleId,
      kind: message.kind,
      opponentSessionId: message.opponentSessionId,
      you: message.you,
      foe: message.foe,
      bag,
    };
    this.scene.pause(SCENE_KEYS.WORLD_SCENE);
    this.scene.launch(SCENE_KEYS.PVP_BATTLE_SCENE, dataToPass);
    this.scene.bringToTop(SCENE_KEYS.PVP_BATTLE_SCENE);
  }
}
